/* eslint-disable */
/**
 * OrkaTaskCardV3Preview — canonical V3 Base Task Card
 * ---------------------------------------------------
 * Source: user-provided exact code (Feb 2026 — "V3 Base Task Card").
 * Preserved verbatim for the design-system showcase. CSS is scoped at runtime
 * to `.orka-v3-scope` to avoid colliding with generic class names (.card,
 * .panel, .page, .icon, .badge, etc.) that are used throughout the main app.
 *
 * Exports:
 *   - default: OrkaTaskCardV3Preview  (wrapped full showcase with width slider)
 *   - named:   V3TaskCard             (single connected card — drives Dashboard 2)
 *   - named:   useV3TaskCardPreviewStyles () => void  (one-shot global CSS loader)
 *   - named:   V3_PRIORITIES, V3_EFFORTS, V3_STATUSES
 *              V3_WORK_AREAS (demo), V3_PERSONAL_SUB_AREAS (demo)
 *              getV3WidthMode
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import { Check } from "lucide-react";
import {
  V3_PRIORITY_COLORS,
  V3_PRIORITY_LABEL,
  V3_PRIORITY_SHORT,
  V3_EFFORT_COLORS,
  V3_EFFORT_LABEL,
  V3_STATUS_COLORS,
  V3_STATUS_LABEL,
} from "../design-system-v3/v3Shared";

const cx = (...parts) => parts.filter(Boolean).join(" ");

function SvgIcon({ children, className = "icon", ...props }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true" {...props}>
      {children}
    </svg>
  );
}

const Icons = {
  Pencil: (p) => <SvgIcon {...p}><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z" /></SvgIcon>,
  Chevron: (p) => <SvgIcon {...p}><path d="m6 9 6 6 6-6" /></SvgIcon>,
  Grip: (p) => <SvgIcon {...p}><path d="M9 5h.01M15 5h.01M9 12h.01M15 12h.01M9 19h.01M15 19h.01" /></SvgIcon>,
  Calendar: (p) => <SvgIcon {...p}><rect x="4" y="5" width="16" height="15" rx="2" /><path d="M8 3v4" /><path d="M16 3v4" /><path d="M4 10h16" /></SvgIcon>,
  Clock: (p) => <SvgIcon {...p}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l4 2" /></SvgIcon>,
  Briefcase: (p) => <SvgIcon {...p}><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M3 12h18" /></SvgIcon>,
  Flag: (p) => <SvgIcon {...p}><path d="M5 21V5" /><path d="M5 5c4-2 7 2 11 0v9c-4 2-7-2-11 0" /></SvgIcon>,
  Gauge: (p) => <SvgIcon {...p}><path d="M4 14a8 8 0 1 1 16 0" /><path d="M12 14l4-4" /><path d="M12 14h.01" /><path d="M5 20h14" /></SvgIcon>,
  Users: (p) => <SvgIcon {...p}><path d="M16 21a6 6 0 0 0-12 0" /><circle cx="10" cy="8" r="4" /><path d="M22 21a5 5 0 0 0-5-5" /><path d="M17 4a4 4 0 0 1 0 8" /></SvgIcon>,
  Repeat: (p) => <SvgIcon {...p}><path d="M17 2l4 4-4 4" /><path d="M3 11V9a3 3 0 0 1 3-3h15" /><path d="M7 22l-4-4 4-4" /><path d="M21 13v2a3 3 0 0 1-3 3H3" /></SvgIcon>,
  Video: (p) => <SvgIcon {...p}><rect x="3" y="6" width="14" height="12" rx="2" /><path d="m17 10 4-3v10l-4-3" /></SvgIcon>,
  Mail: (p) => <SvgIcon {...p}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></SvgIcon>,
  Hash: (p) => <SvgIcon {...p}><path d="M4 9h16" /><path d="M4 15h16" /><path d="M10 3 8 21" /><path d="m16 3-2 18" /></SvgIcon>,
  Sliders: (p) => <SvgIcon {...p}><path d="M4 6h10" /><path d="M18 6h2" /><circle cx="16" cy="6" r="2" /><path d="M4 12h2" /><path d="M10 12h10" /><circle cx="8" cy="12" r="2" /><path d="M4 18h12" /><path d="M20 18h0" /><circle cx="18" cy="18" r="2" /></SvgIcon>,
  Search: (p) => <SvgIcon {...p}><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></SvgIcon>,
};

const DEMO_TASK = {
  id: "TSK-1048",
  title: "ESOP Sense Check - Mark B",
  date: "2026-05-05",
  start: "08:30",
  end: "09:30",
  created: "2026-05-04",
  rescheduleCount: 2,
  description: "Sense-check the ESOP framing before sending Mark the investor-facing note. Confirm whether the suggested option language is sufficiently clear, fair, and aligned with the current employee incentive policy.",
  owner: "Joseph",
};

/* Priority + Effort + Status — single source of truth.
 * Labels, shorts and colors are derived from v3Shared.js so that V5 task
 * cards, V3 task-form modals and any other surfaces stay locked to the
 * SAME canonical map. The list shape kept here is `[{value, short, label, color}]`
 * because the V5 card's TokenDropdown component already iterates that way. */
export const V3_PRIORITIES = [-1, 1, 2, 3, 4, 5].map((v) => ({
  value: v,
  short: V3_PRIORITY_SHORT[v],
  // Priority label in v3Shared is "P1 · Critical" — we strip the leading
  // "Pn · " so the dropdown shows just the human label and the short pill
  // shows "Pn".
  label: (V3_PRIORITY_LABEL[v] || "").replace(/^P[-\d]+\s*·\s*/, "").trim() || V3_PRIORITY_LABEL[v] || "",
  color: V3_PRIORITY_COLORS[v],
}));

export const V3_EFFORTS = [1, 2, 3, 5, 8, 13].map((v) => ({
  value: v,
  short: `E${v}`,
  label: V3_EFFORT_LABEL[v],
  color: V3_EFFORT_COLORS[v],
}));

export const V3_STATUSES = ["todo", "inProgress", "done", "blocked"].map((k) => ({
  value: k,
  label: V3_STATUS_LABEL[k],
  color: V3_STATUS_COLORS[k],
}));

export const V3_WORK_AREAS = [
  { work_area_id: "sales",    short: "SALES", label: "Sales",      color: "#f97316", is_personal: false },
  { work_area_id: "product",  short: "PROD",  label: "Product",    color: "#3b82f6", is_personal: false },
  { work_area_id: "growth",   short: "GROW",  label: "Growth",     color: "#22c55e", is_personal: false },
  { work_area_id: "ops",      short: "OPS",   label: "Operations", color: "#d97706", is_personal: false },
  { work_area_id: "identity", short: "ID",    label: "Identity",   color: "#7c3aed", is_personal: false },
  { work_area_id: "personal", short: "PERS",  label: "Personal",   color: "#0f766e", is_personal: true  },
];

export const V3_PERSONAL_SUB_AREAS = [
  { sub_area_id: "health",  label: "Health" },
  { sub_area_id: "family",  label: "Family" },
  { sub_area_id: "finance", label: "Finance" },
  { sub_area_id: "garden",  label: "Garden" },
  { sub_area_id: "travel",  label: "Travel" },
];

const DEMO_PEOPLE = ["Joseph", "Matt", "Roman", "Olivia", "Denver", "Jamie", "Trek", "Mark B", "Sarah", "Nina"];

const VARIANTS = [
  { key: "light-15min",    theme: "light", density: "15min",    label: "Light · 15min (E1)"  },
  { key: "light-30min",    theme: "light", density: "30min",    label: "Light · 30min (E2)"  },
  { key: "light-60min",    theme: "light", density: "60min",    label: "Light · 60min (E3)"  },
  { key: "light-180min",   theme: "light", density: "180min",   label: "Light · 180min (E5)" },
  { key: "light-360min",   theme: "light", density: "360min",   label: "Light · 360min (E8)" },
  { key: "light-600min",   theme: "light", density: "600min",   label: "Light · 600min (E13)" },
  { key: "dark-60min",     theme: "dark",  density: "60min",    label: "Dark · 60min (E3)"   },
  { key: "dark-180min",    theme: "dark",  density: "180min",   label: "Dark · 180min (E5)"  },
  { key: "light-standard", theme: "light", density: "standard", label: "Light · Standard"     },
  { key: "dark-standard",  theme: "dark",  density: "standard", label: "Dark · Standard"      },
  { key: "light-calm",     theme: "light", density: "calm",     label: "Light · Calm"         },
  { key: "dark-calm",      theme: "dark",  density: "calm",     label: "Dark · Calm"          },
];

/** Map a task's effort score → the canonical density key. */
export function densityForEffort(effortOrMinutes, isMinutes = false) {
  const minutes = isMinutes
    ? Number(effortOrMinutes)
    : ({ 1: 15, 2: 30, 3: 60, 5: 180, 8: 360, 13: 600 }[Number(effortOrMinutes)] || 60);
  if (minutes <= 15) return "15min";
  if (minutes <= 30) return "30min";
  if (minutes <= 60) return "60min";
  if (minutes <= 180) return "180min";
  if (minutes <= 360) return "360min";
  return "600min";
}

function resolve(list, value, fallback = 0, key = "value") {
  return list.find((item) => item[key] === value || item[key] === Number(value)) || list[fallback];
}

function cssVars(entries) {
  const style = {};
  entries.forEach(([name, value]) => { style[name] = value; });
  return style;
}

export function getV3WidthMode(width) {
  if (width < 470) return "mobile";
  if (width < 720) return "squashed";
  return "regular";
}

function safeOpenDateInput(input) {
  if (!input) return false;
  try {
    if (typeof input.showPicker === "function") { input.showPicker(); return true; }
  } catch (e) { /* ignore */ }
  input.focus();
  return false;
}

/* —————————— Raw CSS (unchanged from user spec, scoped at runtime) —————————— */
const RAW_CSS = `
*{box-sizing:border-box}button,input,select,textarea{font:inherit}.preview{min-height:100%;background:#08111e;color:#f8fafc;font-family:Inter,ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif}.page{max-width:1500px;margin:0 auto;padding:28px}.intro{margin-bottom:22px}.intro h1{margin:0;font-size:34px;line-height:1;letter-spacing:-.045em}.intro p{margin:10px 0 0;color:#94a3b8;font-size:14px;line-height:1.6}.controls{display:grid;gap:14px;margin-bottom:26px;padding:18px;border-radius:22px;background:#0f1728;border:1px solid rgba(255,255,255,.08)}.controls-top{display:flex;justify-content:space-between;gap:12px;align-items:center;flex-wrap:wrap}.control-label{font-size:13px;font-weight:900;color:#e2e8f0}.control-meta{display:flex;gap:8px;align-items:center;flex-wrap:wrap}.badge{display:inline-flex;align-items:center;height:30px;border-radius:999px;padding:0 12px;background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.10);color:#cbd5e1;font-size:12px;font-weight:850}.range{width:100%}.variant-grid{display:grid;grid-template-columns:1fr;gap:18px}.panel{width:100%;border-radius:26px;padding:18px;border:1px solid var(--panel-border);background:var(--panel-bg);box-shadow:0 18px 48px rgba(0,0,0,.18);margin-bottom:18px}.panel[data-theme="light"]{--panel-bg:#eef3f8;--panel-border:rgba(15,23,42,.10);--frame-bg:#f7fafc;--frame-border:rgba(15,23,42,.10);--label-bg:#fff;--label-text:#475569;--surface:#fff;--surface2:#f8fafc;--border:rgba(15,23,42,.14);--text:#07111f;--text2:#334155;--text3:#64748b;--shadow:rgba(15,23,42,.12)}.panel[data-theme="dark"]{--panel-bg:#0e1726;--panel-border:rgba(255,255,255,.10);--frame-bg:#0b1220;--frame-border:rgba(255,255,255,.10);--label-bg:rgba(255,255,255,.06);--label-text:#cbd5e1;--surface:#101928;--surface2:#152032;--border:rgba(255,255,255,.12);--text:#f8fafc;--text2:#d7dfeb;--text3:#94a3b8;--shadow:rgba(0,0,0,.42)}.variant-label{display:inline-flex;align-items:center;height:28px;border-radius:999px;padding:0 10px;background:var(--label-bg);border:1px solid var(--frame-border);color:var(--label-text);font-size:11px;font-weight:950;text-transform:uppercase;letter-spacing:.08em;margin-bottom:14px}.variant-frame{width:100%;max-width:100%;border-radius:22px;padding:14px;background:var(--frame-bg);border:1px dashed var(--frame-border);overflow:hidden}.variant-width-note{margin-top:10px;color:var(--label-text);font-size:12px;font-weight:800}.icon{width:16px;height:16px;flex:none}.card{--priority-color:#ea580c;--workarea-color:#f97316;--status-color:#64748b;--cardPad:18px;--radius:22px;--innerRadius:18px;--gripRadius:15px;--controlRadius:14px;--gripW:60px;--barW:6px;--titleSize:22px;--tokenH:34px;--statusH:54px;--statusW:168px;--statusRadius:18px;position:relative;overflow:hidden;border-radius:var(--radius);border:1px solid color-mix(in srgb,var(--priority-color) 30%,var(--border));background:linear-gradient(180deg,color-mix(in srgb,var(--priority-color) 10%,var(--surface)),var(--surface));box-shadow:0 18px 40px var(--shadow)}.density-standard{box-shadow:0 22px 52px color-mix(in srgb,var(--priority-color) 18%,var(--shadow))}.density-calm{--cardPad:20px;--gripW:52px;--barW:4px;--titleSize:20px;--tokenH:30px;--statusH:46px;--statusW:138px;--statusRadius:16px;background:linear-gradient(180deg,color-mix(in srgb,var(--priority-color) 4%,var(--surface)),var(--surface));filter:saturate(.92)}.edit{position:absolute;top:12px;right:12px;z-index:3;width:28px;height:28px;border-radius:10px;border:1px solid var(--border);background:color-mix(in srgb,var(--surface) 86%,transparent);color:var(--text3);display:grid;place-items:center;cursor:pointer;opacity:.84}.edit .icon{width:13px;height:13px}.main{display:grid;grid-template-columns:var(--gripW) var(--barW) minmax(0,1fr);gap:16px;align-items:start;padding:var(--cardPad)}.grip{height:100%;min-height:110px;border-radius:var(--gripRadius);border:1px solid var(--border);background:color-mix(in srgb,var(--surface2) 88%,transparent);display:grid;place-items:center;color:var(--text3);box-shadow:0 8px 18px var(--shadow);cursor:grab}.bar{align-self:stretch;border-radius:999px;background:linear-gradient(180deg,var(--workarea-color),color-mix(in srgb,var(--workarea-color) 70%,#000));box-shadow:0 0 18px color-mix(in srgb,var(--workarea-color) 24%,transparent)}.header-content{min-width:0;display:grid;gap:12px}.title-row{display:flex;gap:5px;align-items:center;margin-right:44px;border-radius:var(--controlRadius)}.meeting-title-icon{width:17px;height:17px;border-radius:5px;border:1px solid color-mix(in srgb,#2563eb 55%,var(--border));background:color-mix(in srgb,#2563eb 8%,var(--surface));color:#1d4ed8;display:grid;place-items:center;flex:none;opacity:.9}.panel[data-theme="dark"] .meeting-title-icon{color:#93c5fd;border-color:color-mix(in srgb,#93c5fd 45%,var(--border));background:color-mix(in srgb,#2563eb 14%,var(--surface))}.meeting-title-icon .icon{width:10px;height:10px}.title-row.editing{background:#fff;border:1px solid rgba(15,23,42,.18);padding:8px}.title-input{width:100%;min-width:0;flex:1;border:0;outline:0;background:transparent;color:var(--text);font-size:var(--titleSize);line-height:1.16;font-weight:950;letter-spacing:-.035em;padding:0}.title-row.editing .title-input{color:#07111f}.title-actions{display:none;gap:6px}.title-row.editing .title-actions{display:flex}
/* When the title is being inline-edited, hide the absolutely-positioned
   edit pencil and the card-toggle chevron so the save/cancel chips never
   overlap them (Feb 2026 fix — the chips used to collide with both icons
   on small densities). */
.card:has(.title-row.editing) > .edit,
.card:has(.title-row.editing) .card-toggle{display:none !important}.inline-action{width:28px;height:28px;border-radius:10px;border:1px solid var(--border);background:var(--surface2);font-size:16px;font-weight:950;display:grid;place-items:center;cursor:pointer}.save-title{color:#059669}.cancel-title{color:#dc2626}.header-meta{display:flex;gap:10px;flex-wrap:wrap;color:var(--text3);font-size:12px;font-weight:850}.header-meta span{display:inline-flex;align-items:center;gap:6px}.header-date-picker{border:0;background:transparent;color:var(--text3);font-size:12px;font-weight:850;outline:0;padding:0;width:104px;cursor:pointer}.header-date-picker::-webkit-calendar-picker-indicator{opacity:0;display:none}.date-picker-wrap{display:inline-flex;align-items:center;gap:2px}.date-chevron{width:12px;height:12px;color:var(--text3);opacity:.7;pointer-events:none}.date-chevron-input-wrap{position:relative;display:grid;place-items:center;width:18px;height:18px;cursor:pointer}.date-chevron-input-wrap:hover .date-chevron{opacity:1}.date-arrow-input{position:absolute;inset:0;width:18px;height:18px;opacity:0;cursor:pointer;color:transparent;background:transparent;border:0}.date-arrow-input::-webkit-calendar-picker-indicator{position:absolute;inset:0;width:18px;height:18px;opacity:0;cursor:pointer}.header-controls{display:block}.actions{display:grid;grid-template-columns:minmax(160px,1fr) auto auto;align-items:start;gap:9px;min-width:0;width:100%}.workarea-stack{display:flex;gap:7px;align-items:center;min-width:0}.subarea-token select{min-width:92px}.pe-stack{display:grid;grid-template-columns:auto auto;gap:9px;min-width:0}.status-zone{display:grid;gap:4px;justify-items:end}.card-toggle{height:22px;width:42px;justify-self:end;border:1px solid color-mix(in srgb,var(--border) 72%,transparent);border-radius:11px;background:color-mix(in srgb,var(--surface2) 82%,transparent);color:var(--text3);display:grid;place-items:center;cursor:pointer;opacity:.78;padding:0}.card-toggle:hover{opacity:1}.card-toggle .icon{width:14px;height:14px;transition:transform .16s ease}.card-toggle.open .icon{transform:rotate(180deg)}.token-select{--token-color:#64748b;height:var(--tokenH);border-radius:999px;border:1.5px solid color-mix(in srgb,var(--token-color) 72%,var(--border));background:color-mix(in srgb,var(--token-color) 9%,var(--surface2));display:inline-flex;align-items:center;gap:7px;padding:0 9px;color:var(--token-color);font-size:12px;font-weight:950;white-space:nowrap;min-width:0;overflow:hidden}.token-dot{width:8px;height:8px;border-radius:999px;background:var(--token-color);box-shadow:0 0 0 3px color-mix(in srgb,var(--token-color) 16%,transparent);flex:none}.token-select select{appearance:none;border:0;outline:0;background:transparent;color:var(--token-color);font:inherit;font-weight:950;cursor:pointer;min-width:72px;max-width:132px;padding-right:12px}.token-select option{color:#0f172a;background:#fff}.workarea-token select{min-width:82px}.effort-token select{min-width:68px;max-width:98px}.density-calm .priority-token{display:none}.status{height:var(--statusH);min-width:var(--statusW);border:0;border-radius:var(--statusRadius);background:linear-gradient(180deg,var(--status-color),color-mix(in srgb,var(--status-color) 72%,#000));color:#fff;display:inline-flex;align-items:center;justify-content:space-between;gap:10px;padding:0 16px;font-size:16px;font-weight:950;box-shadow:0 12px 24px color-mix(in srgb,var(--status-color) 28%,transparent);cursor:pointer}.status-ring{width:22px;height:22px;border-radius:999px;background:#fff;box-shadow:inset 0 0 0 7px color-mix(in srgb,var(--status-color) 18%,#fff);flex:none}.status-morph{position:relative;width:100%;min-width:var(--statusW);border-radius:var(--statusRadius);border:1px solid color-mix(in srgb,var(--status-color) 50%,var(--border));background:color-mix(in srgb,var(--surface) 94%,transparent);box-shadow:0 12px 24px color-mix(in srgb,var(--status-color) 18%,transparent);overflow:hidden}.status-morph-close{position:absolute;top:6px;right:6px;z-index:2;height:22px;border:0;border-radius:8px;background:color-mix(in srgb,var(--surface) 86%,transparent);color:var(--text3);font-size:11px;font-weight:900;padding:0 8px;opacity:.82}.status-grid{display:grid;grid-template-columns:1fr 1fr;gap:1px;background:var(--border);padding:1px}.status-choice{min-height:62px;border:0;background:color-mix(in srgb,var(--choice-color) 11%,var(--surface));color:var(--text);padding:10px;display:flex;align-items:center;justify-content:center;gap:8px;text-align:center;cursor:pointer;border-radius:0}.status-choice:nth-child(1){border-top-left-radius:calc(var(--statusRadius) - 2px)}.status-choice:nth-child(2){border-top-right-radius:calc(var(--statusRadius) - 2px)}.status-choice:nth-child(3){border-bottom-left-radius:calc(var(--statusRadius) - 2px)}.status-choice:nth-child(4){border-bottom-right-radius:calc(var(--statusRadius) - 2px)}.status-choice:hover,.status-choice.active{background:color-mix(in srgb,var(--choice-color) 21%,var(--surface));box-shadow:inset 0 0 0 2px var(--choice-color)}.status-dot{width:12px;height:12px;border-radius:999px;background:var(--choice-color);box-shadow:0 0 0 3px color-mix(in srgb,var(--choice-color) 14%,transparent);flex:none}.status-label{font-size:12px;font-weight:950;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.mode-squashed{--cardPad:14px;--gripW:38px;--barW:5px;--titleSize:clamp(17px,3.2vw,20px);--tokenH:31px;--statusH:44px;--statusW:0px;--statusRadius:15px}.mode-squashed .main{grid-template-columns:38px 5px minmax(0,1fr);gap:9px;align-items:start}.mode-squashed .grip{min-height:86px;border-radius:13px}.mode-squashed .header-content{gap:10px}.mode-squashed .title-row{margin-right:34px;min-width:0}.mode-squashed .title-input{overflow:hidden;text-overflow:ellipsis}.mode-squashed .edit{top:10px;right:10px;width:26px;height:26px}.mode-squashed .header-meta{gap:7px;font-size:11px;line-height:1.2}.mode-squashed .header-meta .icon{width:14px;height:14px}.mode-squashed .header-date-picker{width:92px;font-size:11px}.mode-squashed .header-controls{display:block}.mode-squashed .actions{display:grid;grid-template-columns:minmax(0,1fr) minmax(118px,.7fr) minmax(104px,.62fr);gap:6px;margin-left:0;width:100%;align-items:start}.mode-squashed .workarea-stack{display:grid;grid-template-columns:minmax(0,1fr);gap:5px;min-width:0}.mode-squashed .workarea-stack.has-subarea{grid-template-columns:minmax(0,1fr) minmax(82px,.72fr)}.mode-squashed .pe-stack{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:5px;width:100%;min-width:0}.mode-squashed .token-select{justify-content:flex-start;padding:0 6px;font-size:10.5px;width:100%;gap:4px;min-width:0}.mode-squashed .token-select .icon{display:none}.mode-squashed .token-dot{width:7px;height:7px;box-shadow:0 0 0 2px color-mix(in srgb,var(--token-color) 16%,transparent)}.mode-squashed .token-select select{min-width:0;max-width:none;width:100%;padding-right:7px;overflow:hidden;text-overflow:ellipsis}.mode-squashed .subarea-token select{min-width:0}.mode-squashed .status-zone{min-width:0;width:100%;justify-items:stretch}.mode-squashed .status,.mode-squashed .status-morph{width:100%;min-width:0}.mode-squashed .status{height:31px;border-radius:999px;padding:0 8px;font-size:10.5px;gap:6px;box-shadow:0 8px 16px color-mix(in srgb,var(--status-color) 20%,transparent)}.mode-squashed .status-ring{width:15px;height:15px;box-shadow:inset 0 0 0 5px color-mix(in srgb,var(--status-color) 18%,#fff)}.mode-squashed .card-toggle{width:100%;height:19px;border-radius:999px}.mode-squashed .status-choice{min-height:44px;padding:7px 6px;gap:5px}.mode-squashed .status-dot{width:9px;height:9px;box-shadow:0 0 0 2px color-mix(in srgb,var(--choice-color) 14%,transparent)}.mode-squashed .status-label{font-size:10.5px}.mode-squashed.density-calm .actions{grid-template-columns:minmax(0,1fr) minmax(84px,.42fr) minmax(102px,.55fr)}.mode-squashed.density-calm .pe-stack{grid-template-columns:1fr}.mode-squashed.density-calm .effort-token{height:31px}.mode-squashed.squash-tight{--cardPad:12px;--gripW:30px;--titleSize:16px;--tokenH:30px;--statusRadius:14px}.mode-squashed.squash-tight .main{grid-template-columns:30px 4px minmax(0,1fr);gap:7px}.mode-squashed.squash-tight .grip{min-height:78px;border-radius:12px}.mode-squashed.squash-tight .bar{width:4px}.mode-squashed.squash-tight .header-content{gap:9px}.mode-squashed.squash-tight .title-row{margin-right:31px}.mode-squashed.squash-tight .header-meta{gap:6px;font-size:10.5px}.mode-squashed.squash-tight .header-date-picker{width:88px;font-size:10.5px}.mode-squashed.squash-tight .actions{grid-template-columns:minmax(0,1fr) minmax(92px,.62fr);grid-template-areas:"work work" "pe status";gap:6px}.mode-squashed.squash-tight .workarea-stack{grid-area:work}.mode-squashed.squash-tight .pe-stack{grid-area:pe}.mode-squashed.squash-tight .status-zone{grid-area:status}.mode-squashed.squash-tight .token-select{height:30px;font-size:10px;padding:0 6px}.mode-squashed.squash-tight .status{height:30px;font-size:10px;padding:0 7px}.mode-squashed.squash-tight .status-ring{width:14px;height:14px}.mode-squashed.squash-tight .card-toggle{height:18px}.mode-squashed.squash-tight .status-choice{min-height:42px;padding:6px 5px}.mode-squashed.squash-tight .status-label{font-size:10px}.mode-squashed.squash-tight .open-body{padding:12px;gap:10px}.mode-squashed.squash-tight .meta-strip{gap:8px;font-size:10.5px}.mode-squashed.squash-tight .description-toolbar{padding:6px;gap:5px}.mode-squashed.squash-tight .tool-btn{min-width:28px;height:27px;font-size:11px;padding:0 7px}.mode-squashed.squash-tight .section-block{padding:11px}.mode-squashed.squash-tight .action-row{flex-wrap:wrap}.mode-squashed.squash-tight .shortcut{width:100%}.mode-squashed.squash-tight .cancel-btn,.mode-squashed.squash-tight .save-btn{flex:1;min-width:0}.mobile-card{display:none}.card.mode-mobile{display:none}.mobile-card.mode-mobile{display:block;position:relative;overflow:hidden;border-radius:var(--radius);border:1px solid color-mix(in srgb,var(--priority-color) 30%,var(--border));background:linear-gradient(180deg,color-mix(in srgb,var(--priority-color) 10%,var(--surface)),var(--surface));box-shadow:0 12px 28px var(--shadow)}.mobile-card .edit{display:block}.mobile-top{display:grid;grid-template-columns:34px 4px minmax(0,1fr);gap:10px;align-items:start;padding:14px}.mobile-grip{height:48px;border-radius:12px;border:1px solid var(--border);background:var(--surface2);display:grid;place-items:center;color:var(--text3)}.mobile-bar{align-self:stretch;border-radius:999px;background:linear-gradient(180deg,var(--workarea-color),color-mix(in srgb,var(--workarea-color) 70%,#000))}.mobile-title-wrap{display:grid;gap:6px}.mobile-title-wrap .title-row{margin-right:44px}.mobile-controls{display:grid;grid-template-columns:1fr 1fr;gap:8px;padding:0 14px 14px}.mobile-controls .status-zone{grid-column:1/-1}.mobile-workarea{grid-column:1/-1}.mobile-controls .token-select{justify-content:center;padding:0 8px;width:100%;overflow:hidden}.mobile-controls .token-select select{min-width:0;max-width:none;width:100%;overflow:hidden;text-overflow:ellipsis}.mobile-controls .token-select .icon{display:none}.mobile-status{grid-column:1/-1;height:42px;border:0;border-radius:15px;background:linear-gradient(180deg,var(--status-color),color-mix(in srgb,var(--status-color) 72%,#000));color:#fff;display:flex;align-items:center;justify-content:space-between;padding:0 14px;font-weight:950}.mobile-status-morph{grid-column:1/-1;min-width:0}.open-body{display:grid;gap:12px;padding:14px 18px 18px;border-top:1px solid var(--border);background:color-mix(in srgb,var(--surface2) 62%,transparent);overflow:visible;border-bottom-left-radius:calc(var(--radius) - 1px);border-bottom-right-radius:calc(var(--radius) - 1px)}.section-block{position:relative;overflow:visible;border:1px solid var(--border);border-radius:var(--innerRadius);background:var(--surface);padding:13px}.meta-strip{display:flex;gap:12px;flex-wrap:wrap;color:var(--text3);font-size:11px;font-weight:850;align-items:center}.hidden-check{margin-left:auto;display:inline-flex;align-items:center;gap:6px;color:var(--text3);opacity:.72;font-size:11px;font-weight:850}.hidden-check input{width:13px;height:13px}.meta-subtle{display:inline-flex;align-items:center;gap:6px}.description-editor{border:1px solid var(--border);border-radius:var(--innerRadius);background:var(--surface);overflow:hidden}.description-toolbar{min-height:40px;border-bottom:1px solid var(--border);display:flex;align-items:center;gap:6px;padding:7px 10px;background:color-mix(in srgb,var(--surface2) 75%,transparent);flex-wrap:wrap}.tool-btn{min-width:30px;height:28px;border-radius:9px;border:1px solid var(--border);background:var(--surface);color:var(--text2);display:grid;place-items:center;padding:0 8px;cursor:pointer;font-size:12px;font-weight:850}.tool-btn:hover,.tool-btn.active{background:var(--surface2);color:var(--text)}.toolbar-separator{width:1px;height:22px;background:var(--border);margin:0 2px}.block-editor-shell{display:grid;grid-template-columns:140px minmax(0,1fr);min-height:190px;background:var(--surface)}.block-sidebar{border-right:1px solid var(--border);background:color-mix(in srgb,var(--surface2) 55%,transparent);padding:10px;display:grid;align-content:start;gap:7px}.block-type-btn{height:30px;border-radius:10px;border:1px solid var(--border);background:var(--surface);color:var(--text2);font-size:11px;font-weight:900;text-align:left;padding:0 9px;cursor:pointer}.block-type-btn:hover{background:var(--surface2);color:var(--text)}.description-box{width:100%;min-height:190px;max-height:520px;padding:14px;color:var(--text2);font-size:13px;line-height:1.6;outline:0;resize:vertical;overflow:auto;border:0;background:var(--surface)}.description-box h2,.description-box h3{color:var(--text);margin:0 0 8px}.description-box p{margin:0 0 8px}.description-box ul,.description-box ol{margin:0 0 8px 20px;padding:0}.description-box blockquote{margin:0 0 8px;padding:8px 12px;border-left:3px solid var(--workarea-color);background:color-mix(in srgb,var(--workarea-color) 7%,var(--surface2));border-radius:10px}.description-code{width:100%;min-height:230px;max-height:560px;padding:14px;color:var(--text2);font-family:ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,"Liberation Mono","Courier New",monospace;font-size:12px;line-height:1.55;outline:0;resize:vertical;overflow:auto;border:0;background:var(--surface)}@media(max-width:760px){.block-editor-shell{grid-template-columns:1fr}.block-sidebar{border-right:0;border-bottom:1px solid var(--border);grid-template-columns:repeat(2,minmax(0,1fr))}}.date-time-grid,.logic-grid,.advanced-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px}.field-input{border:1px solid var(--border);border-radius:var(--controlRadius);background:var(--surface);padding:9px}.field-input span{display:block;color:var(--text3);font-size:10px;text-transform:uppercase;letter-spacing:.08em;font-weight:950}.field-input input,.field-input select,.field-input textarea{width:100%;margin-top:4px;border:0;background:transparent;color:var(--text);outline:0;font-size:13px;font-weight:850}.field-input textarea{min-height:70px;resize:vertical}.wide-field{grid-column:1/-1}.section-block.recurring{border-color:color-mix(in srgb,#8b5cf6 36%,var(--border));background:color-mix(in srgb,#8b5cf6 5%,var(--surface))}.section-block.meeting{border-color:color-mix(in srgb,#2563eb 30%,var(--border));background:color-mix(in srgb,#2563eb 5%,var(--surface))}.toggle-section{display:flex;align-items:center;justify-content:space-between;gap:10px}.checkbox-row{display:flex;align-items:center;gap:8px;color:var(--text2);font-size:14px;font-weight:850}.section-expander-arrow{width:26px;height:26px;border:0;border-radius:10px;background:transparent;color:var(--text3);display:grid;place-items:center;cursor:pointer;opacity:.72}.section-expander-arrow:hover{opacity:1;background:color-mix(in srgb,var(--surface2) 70%,transparent)}.section-expander-arrow .icon{width:13px;height:13px;transition:transform .16s ease}.section-expander-arrow.open .icon{transform:rotate(180deg)}.logic-grid{margin-top:12px}.meeting-card{margin-top:12px;border:1px solid color-mix(in srgb,#2563eb 30%,var(--border));border-radius:var(--innerRadius);background:color-mix(in srgb,#bfdbfe 58%,var(--surface));padding:18px;display:grid;gap:12px;color:#1d4ed8}.panel[data-theme="dark"] .meeting-card{background:color-mix(in srgb,#1d4ed8 15%,var(--surface));color:#93c5fd}.meeting-title{display:flex;align-items:center;gap:10px;font-size:16px;font-weight:950;color:#1d4ed8}.panel[data-theme="dark"] .meeting-title{color:#bfdbfe}.meeting-actions{display:flex;gap:8px;flex-wrap:wrap}.meeting-btn{height:44px;border:0;border-radius:var(--controlRadius);color:white;display:inline-flex;align-items:center;gap:8px;padding:0 16px;font-size:15px;font-weight:900;box-shadow:0 8px 16px rgba(15,23,42,.12)}.meeting-btn.meet{background:#059669}.meeting-btn.calendar{background:#2563eb}.meeting-btn.email{background:#334155}.meeting-description{display:grid;gap:6px}.meeting-description span,.meeting-field span,.attendee-head>span{display:flex;align-items:center;gap:6px;color:#1d4ed8;font-size:12px;font-weight:900}.panel[data-theme="dark"] .meeting-description span,.panel[data-theme="dark"] .meeting-field span,.panel[data-theme="dark"] .attendee-head>span{color:#93c5fd}.meeting-description textarea{width:100%;min-height:56px;resize:vertical;border:1px solid color-mix(in srgb,#2563eb 25%,var(--border));border-radius:var(--controlRadius);background:var(--surface);color:var(--text);padding:13px;font-size:16px;outline:0}.meeting-grid{display:grid;grid-template-columns:1fr 1fr;gap:18px}.meeting-field{display:grid;gap:6px}.meeting-field input{height:40px;border:1px solid color-mix(in srgb,#2563eb 25%,var(--border));border-radius:var(--controlRadius);background:var(--surface);color:var(--text);padding:0 12px;outline:0;font-size:14px}.attendee-head{display:flex;align-items:center;justify-content:space-between;gap:10px;flex-wrap:wrap}.rsvp-counts{display:flex;gap:12px;color:var(--text);font-size:12px}.rsvp-counts span{display:inline-flex;align-items:center;gap:5px}.rsvp-counts i{width:8px;height:8px;border-radius:999px;display:inline-block}.rsvp-counts .yes{background:#10b981}.rsvp-counts .maybe{background:#f59e0b}.rsvp-counts .no{background:#ef4444}.invite-row{display:grid;grid-template-columns:minmax(0,1fr) 58px;gap:6px}.invite-row input{height:38px;border:1px solid color-mix(in srgb,#2563eb 25%,var(--border));border-radius:var(--controlRadius);background:var(--surface);color:var(--text);padding:0 12px;outline:0}.invite-row button{border:0;border-radius:var(--controlRadius);background:#8fb0f8;color:white;display:grid;place-items:center}.attendee-list{border:1px solid color-mix(in srgb,#2563eb 20%,var(--border));border-radius:var(--controlRadius);background:var(--surface);overflow:hidden}.attendee-row{display:grid;grid-template-columns:14px minmax(0,1fr) auto auto;gap:10px;align-items:center;padding:12px 14px;border-bottom:1px solid var(--border)}.attendee-row:last-child{border-bottom:0}.attendee-dot{width:11px;height:11px;border-radius:999px;background:#10b981}.attendee-row strong{display:block;color:var(--text);font-size:14px}.attendee-row span{display:block;color:var(--text3);font-size:12px}.attendee-row em{font-style:normal;background:#dbeafe;color:#1d4ed8;padding:6px 12px;border-radius:var(--controlRadius);font-size:11px;font-weight:900}.attendee-row b{background:#bbf7d0;color:#047857;padding:6px 12px;border-radius:var(--controlRadius);font-size:13px}.meeting-event-id{border-top:1px solid color-mix(in srgb,#2563eb 20%,var(--border));padding-top:10px;color:var(--text3);font-size:11px;font-weight:850;display:flex;gap:6px;align-items:center;overflow-wrap:anywhere}.section-head{display:flex;align-items:center;justify-content:space-between;gap:9px;color:var(--text);font-weight:950}.section-head-left{display:flex;align-items:center;gap:9px}.people-grid{display:grid;grid-template-columns:minmax(170px,.8fr) minmax(260px,1.6fr);gap:10px;margin-top:10px;position:relative;overflow:visible}.person-field{display:grid;gap:7px;position:relative;overflow:visible}.person-label{color:var(--text3);font-size:10px;text-transform:uppercase;letter-spacing:.08em;font-weight:950}.responsible-picker,.observer-picker{position:relative}.responsible-trigger,.observer-trigger{width:100%;min-height:40px;border:1px solid var(--border);border-radius:var(--controlRadius);background:var(--surface2);color:var(--text);display:flex;align-items:center;justify-content:space-between;gap:8px;padding:6px 10px;font-size:12px;font-weight:900}.chips{display:flex;gap:6px;flex-wrap:wrap}.person-chip{height:24px;border-radius:999px;background:color-mix(in srgb,var(--workarea-color) 12%,var(--surface));border:1px solid color-mix(in srgb,var(--workarea-color) 35%,var(--border));color:var(--text);padding:0 8px;display:inline-flex;align-items:center;font-size:11px;font-weight:900}.responsible-menu,.observer-menu{position:absolute;top:calc(100% + 8px);left:0;right:0;z-index:40;border:1px solid var(--border);border-radius:var(--innerRadius);background:var(--surface);padding:10px;box-shadow:0 18px 42px var(--shadow)}.search-row{height:34px;border:1px solid var(--border);border-radius:var(--controlRadius);background:var(--surface2);display:flex;align-items:center;gap:7px;padding:0 9px;color:var(--text3)}.search-row input{min-width:0;flex:1;border:0;background:transparent;color:var(--text);outline:0;font-size:12px}.person-list{display:grid;gap:5px;margin-top:8px;max-height:154px;overflow:auto}.person-option{height:30px;border:0;border-radius:10px;background:transparent;color:var(--text2);display:flex;align-items:center;justify-content:space-between;padding:0 8px;font-size:12px;font-weight:850}.person-option.active{background:color-mix(in srgb,var(--workarea-color) 12%,var(--surface));color:var(--text)}.expander{border:1px solid var(--border);border-radius:var(--innerRadius);background:var(--surface);overflow:hidden}.expander button{width:100%;height:44px;border:0;background:var(--surface);color:var(--text);display:flex;align-items:center;justify-content:space-between;padding:0 13px;font-weight:950}.expander-body{border-top:1px solid var(--border);padding:13px}.action-row{display:flex;justify-content:flex-end;gap:10px;padding-top:2px}.save-btn,.cancel-btn{height:40px;border-radius:var(--controlRadius);padding:0 16px;font-size:13px;font-weight:950}.save-btn{border:0;background:#6d28d9;color:#fff;box-shadow:0 12px 28px rgba(109,40,217,.28)}.cancel-btn{border:1px solid var(--border);background:var(--surface);color:var(--text2)}.shortcut{margin-right:auto;color:var(--text3);font-size:12px;font-weight:850;align-self:center}@media(max-width:760px){.page{padding:14px}.controls{padding:14px}.variant-frame{padding:10px}.date-time-grid,.people-grid,.meeting-grid,.logic-grid,.advanced-grid{grid-template-columns:1fr}.open-body{padding:12px 14px 14px}.action-row{flex-wrap:wrap}.shortcut{width:100%}.attendee-row{grid-template-columns:14px minmax(0,1fr)}}
`;

/* —————————— Effort-sized densities (15/30/60/180/360/600 min) ——————————
   - HEADER CEILING: every density shares the same compact header layout
     (grip · bar · title+meta+actions). The header is clamped to 120 px.
   - Variants ≤ 60 min collapse the header to fit (60 / 30 px); the
     internal layout adapts (single-line, hide meta, etc).
   - Variants > 60 min show the header at its 120 px ceiling, then the
     interior `.open-body` peeks through underneath with a transparency
     fade (mask-image) until the user clicks the expander.
   - When `.is-expanded` is set on the card, the height clamp + fade are
     removed and the card breaks out to its full natural size, on top of
     siblings (z-index 600).
*/
const CSS_EFFORT_DENSITIES = `
/* Workarea-popover trigger — visually identical to the legacy <select>
   token-select pill, but using a button so the popover can render colored
   work-area swatches in its menu. */
.token-select.workarea-popover-trigger{cursor:pointer;background:color-mix(in srgb,var(--token-color) 9%,var(--surface2));border:1.5px solid color-mix(in srgb,var(--token-color) 72%,var(--border));padding:0 12px 0 9px;font:inherit;outline:0}
.token-select.workarea-popover-trigger:hover{border-color:color-mix(in srgb,var(--token-color) 90%,var(--border))}
.token-select.workarea-popover-trigger .workarea-label{font-weight:950;letter-spacing:.02em;color:var(--token-color);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;min-width:0;max-width:132px}
.density-15min .workarea-popover-trigger .workarea-label{display:none}
.density-30min .workarea-popover-trigger .workarea-label{max-width:62px}
.mode-squashed .workarea-popover-trigger{padding:0 8px}
.mode-squashed .workarea-popover-trigger .workarea-label{max-width:none;width:100%}
.density-15min,.density-30min,.density-60min,.density-180min,.density-360min,.density-600min{
  --cardPad:8px;--radius:14px;--innerRadius:12px;--gripRadius:10px;--controlRadius:10px;
  --gripW:34px;--barW:4px;--titleSize:15px;--tokenH:26px;--statusH:30px;--statusW:102px;
  --statusRadius:999px;--headerCeiling:120px;
  position:relative;overflow:hidden;
  box-shadow:0 10px 24px color-mix(in srgb,var(--priority-color) 14%,var(--shadow));
  height:100%;
}
/* Default-size column for density quotas (used by design-system showcase) */
.density-15min{min-height:30px}
.density-30min{min-height:60px}
.density-60min{min-height:120px}
.density-180min{min-height:360px}
.density-360min{min-height:720px}
.density-600min{min-height:1200px}
/* Common header layout */
.density-15min .main,.density-30min .main,.density-60min .main,
.density-180min .main,.density-360min .main,.density-600min .main{
  grid-template-columns:var(--gripW) var(--barW) minmax(0,1fr);gap:8px;
  padding:var(--cardPad);align-items:stretch;
  height:var(--headerCeiling);max-height:var(--headerCeiling);
}
.density-15min .grip,.density-30min .grip,.density-60min .grip,
.density-180min .grip,.density-360min .grip,.density-600min .grip{
  min-height:0;height:100%;border-radius:var(--gripRadius);
  box-shadow:0 4px 10px var(--shadow);
}
.density-15min .edit,.density-30min .edit,.density-60min .edit,
.density-180min .edit,.density-360min .edit,.density-600min .edit{
  top:6px;right:6px;width:22px;height:22px;border-radius:7px;
}
.density-15min .edit .icon,.density-30min .edit .icon,.density-60min .edit .icon,
.density-180min .edit .icon,.density-360min .edit .icon,.density-600min .edit .icon{
  width:11px;height:11px;
}
.density-15min .header-content,.density-30min .header-content,.density-60min .header-content,
.density-180min .header-content,.density-360min .header-content,.density-600min .header-content{
  gap:6px;align-content:space-between;height:100%;
}
.density-15min .title-row,.density-30min .title-row,.density-60min .title-row,
.density-180min .title-row,.density-360min .title-row,.density-600min .title-row{
  margin-right:32px;min-width:0;gap:5px;
}
.density-15min .title-input,.density-30min .title-input,.density-60min .title-input,
.density-180min .title-input,.density-360min .title-input,.density-600min .title-input{
  line-height:1.08;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;
  font-size:var(--titleSize);letter-spacing:-.02em;
}
.density-15min .header-meta,.density-30min .header-meta,.density-60min .header-meta,
.density-180min .header-meta,.density-360min .header-meta,.density-600min .header-meta{
  gap:8px;font-size:10.5px;line-height:1.2;
}
.density-15min .header-meta .icon,.density-30min .header-meta .icon,.density-60min .header-meta .icon,
.density-180min .header-meta .icon,.density-360min .header-meta .icon,.density-600min .header-meta .icon{
  width:12px;height:12px;
}
.density-15min .header-date-picker,.density-30min .header-date-picker,.density-60min .header-date-picker,
.density-180min .header-date-picker,.density-360min .header-date-picker,.density-600min .header-date-picker{
  width:86px;font-size:10.5px;
}
.density-15min .actions,.density-30min .actions,.density-60min .actions,
.density-180min .actions,.density-360min .actions,.density-600min .actions{
  grid-template-columns:minmax(0,1fr) minmax(0,auto) minmax(0,auto);gap:6px;align-items:center;
}
.density-15min .token-select,.density-30min .token-select,.density-60min .token-select,
.density-180min .token-select,.density-360min .token-select,.density-600min .token-select{
  height:var(--tokenH);padding:0 7px;font-size:10.5px;gap:5px;
}
.density-15min .token-select .icon,.density-30min .token-select .icon,.density-60min .token-select .icon,
.density-180min .token-select .icon,.density-360min .token-select .icon,.density-600min .token-select .icon{
  width:12px;height:12px;
}
.density-15min .status,.density-30min .status,.density-60min .status,
.density-180min .status,.density-360min .status,.density-600min .status{
  height:var(--statusH);min-width:var(--statusW);padding:0 10px;font-size:11px;gap:7px;
  box-shadow:0 6px 12px color-mix(in srgb,var(--status-color) 20%,transparent);
  border-radius:var(--statusRadius);
}
.density-15min .status-ring,.density-30min .status-ring,.density-60min .status-ring,
.density-180min .status-ring,.density-360min .status-ring,.density-600min .status-ring{
  width:14px;height:14px;box-shadow:inset 0 0 0 4px color-mix(in srgb,var(--status-color) 18%,#fff);
}
.density-15min .status-zone,.density-30min .status-zone,.density-60min .status-zone,
.density-180min .status-zone,.density-360min .status-zone,.density-600min .status-zone{
  gap:4px;align-content:center;align-items:center;justify-items:end;
}
/* The card-toggle (chevron) is the EXPANDER for all effort densities. */
.density-15min .card-toggle,.density-30min .card-toggle,.density-60min .card-toggle,
.density-180min .card-toggle,.density-360min .card-toggle,.density-600min .card-toggle{
  display:grid;height:22px;width:42px;justify-self:end;
  /* Always-visible expander — bumped contrast so users can find it. */
  opacity:1;border-color:color-mix(in srgb,var(--priority-color) 38%,var(--border));
  background:color-mix(in srgb,var(--priority-color) 7%,var(--surface));
  color:var(--text2);
}
.density-15min .card-toggle:hover,.density-30min .card-toggle:hover,.density-60min .card-toggle:hover,
.density-180min .card-toggle:hover,.density-360min .card-toggle:hover,.density-600min .card-toggle:hover{
  background:color-mix(in srgb,var(--priority-color) 14%,var(--surface));
}
/* —————— 15 min (single-row: ALL key info crammed into 30 px) —————— */
.density-15min{--headerCeiling:30px;--cardPad:3px;--titleSize:12px;--tokenH:22px;--statusH:22px;--statusW:0px}
.density-15min .main{grid-template-columns:14px 3px minmax(0,1fr);gap:6px;padding:3px 5px}
.density-15min .grip{box-shadow:none;min-height:0}
.density-15min .grip svg{width:10px;height:10px}
.density-15min .bar{border-radius:3px}
.density-15min .edit{display:none}
/* Force the 3-row header into a single inline row */
.density-15min .header-content{display:flex !important;flex-direction:row;align-items:center;gap:6px;height:100%;min-width:0}
.density-15min .title-row{margin:0;gap:4px;flex:1 1 auto;min-width:60px}
.density-15min .title-input{font-size:12px;line-height:1;padding:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.density-15min .meeting-title-icon{width:11px;height:11px;border-radius:3px}
.density-15min .meeting-title-icon .icon{width:7px;height:7px}
.density-15min .header-meta{flex:0 0 auto;gap:0;font-size:10px;line-height:1}
.density-15min .header-meta > span:first-child{display:none}     /* hide date — keep only time */
.density-15min .header-meta > span{padding:0}
.density-15min .header-meta .icon{width:10px;height:10px}
.density-15min .header-controls{flex:0 0 auto;display:flex;align-items:center}
.density-15min .actions{display:flex;flex-direction:row;align-items:center;gap:4px;width:auto}
.density-15min .workarea-stack{gap:0;flex:0 0 auto}
.density-15min .pe-stack{display:flex;flex-direction:row;gap:3px;flex:0 0 auto}
/* Tokens become tiny chips: hide their icon + select arrow, show dot + abbreviated label */
.density-15min .token-select{height:20px;padding:0 6px;font-size:10px;gap:4px;min-width:0;border-width:1px}
.density-15min .token-select .icon{display:none}
.density-15min .token-dot{width:6px;height:6px;box-shadow:0 0 0 2px color-mix(in srgb,var(--token-color) 16%,transparent)}
.density-15min .token-select select{min-width:0;max-width:42px;padding-right:4px;font-size:10px;font-weight:900}
.density-15min .workarea-token select{max-width:36px}
.density-15min .effort-token select{max-width:30px}
.density-15min .status{height:20px;min-width:0;padding:0 7px;font-size:9.5px;gap:5px;border-radius:999px;font-weight:900}
.density-15min .status-ring{width:9px;height:9px;box-shadow:inset 0 0 0 3px color-mix(in srgb,var(--status-color) 18%,#fff)}
.density-15min .status-zone{display:flex;flex-direction:row;align-items:center;gap:3px;justify-items:end}
.density-15min .card-toggle{height:20px;width:22px;display:grid;border-radius:6px}
.density-15min .card-toggle .icon{width:11px;height:11px}
/* —————— 30 min (two compact rows: title+time on top, chips+status on bottom) —————— */
.density-30min{--headerCeiling:60px;--cardPad:5px;--titleSize:13px;--tokenH:22px;--statusH:24px;--statusW:74px}
.density-30min .main{grid-template-columns:24px 3px minmax(0,1fr);gap:7px;padding:5px 7px}
.density-30min .grip{box-shadow:none}
.density-30min .grip svg{width:12px;height:12px}
.density-30min .header-content{gap:3px;height:100%;align-content:center}
.density-30min .title-row{margin-right:0;gap:4px}
.density-30min .title-input{font-size:13px;line-height:1.05;font-weight:950}
.density-30min .header-meta{gap:6px;font-size:9.5px;display:none}     /* date+time would push past 60px ceiling */
.density-30min .actions{display:grid;grid-template-columns:minmax(0,1fr) auto auto auto;gap:5px;align-items:center}
.density-30min .workarea-stack{gap:3px}
.density-30min .pe-stack{grid-template-columns:auto auto;gap:4px}
.density-30min .token-select{height:22px;padding:0 6px;font-size:10px;gap:4px}
.density-30min .token-select .icon{display:none}
.density-30min .token-select select{min-width:0;max-width:62px;padding-right:6px;font-weight:900}
.density-30min .workarea-token select{max-width:54px}
.density-30min .effort-token select{max-width:36px}
.density-30min .status{height:22px;min-width:60px;padding:0 8px;font-size:10px;gap:5px;border-radius:999px}
.density-30min .status-ring{width:10px;height:10px;box-shadow:inset 0 0 0 3px color-mix(in srgb,var(--status-color) 18%,#fff)}
.density-30min .status-zone{gap:3px}
.density-30min .card-toggle{height:22px;width:26px}
.density-30min .card-toggle .icon{width:11px;height:11px}
.density-30min .edit{top:4px;right:4px;width:18px;height:18px}
.density-30min .edit .icon{width:9px;height:9px}
/* —————— 60 min default (already-canonical, just inherits) —————— */
.density-60min{--headerCeiling:120px}
/* Hide body in collapsed state for ≤60min densities (no room) */
.density-15min .open-body,.density-30min .open-body,.density-60min .open-body{
  display:none;
}
/* —————— 180/360/600 min: 60min-style header at the top, then the
   interior body peeks underneath with a transparency fade. The card
   height is the canonical slot (180→360 / 360→720 / 600→1200 px). The
   header is clamped to 120px (the 60-min ceiling); the body fills the
   remaining slot height below it with a mask-image fade. —————— */
.density-180min,.density-360min,.density-600min{
  background:linear-gradient(180deg,
    color-mix(in srgb,var(--priority-color) 10%,var(--surface)),
    var(--surface));
}
.density-180min .open-body,
.density-360min .open-body,
.density-600min .open-body{
  display:grid;gap:12px;padding:14px 18px 18px;
  border-top:1px solid var(--border);
  background:color-mix(in srgb,var(--surface2) 62%,transparent);
  overflow:hidden;pointer-events:none;
  /* Render the body at its NATURAL height (compact), top-anchored, never
     stretching its grid rows to fill the slot. The card box (180/360/600)
     reserves the canonical slot height; the body draws inside that area
     and is clipped + faded at the bottom. The opacity .55 gives the
     whole peek a transparent feel; the linear mask-image fades the bottom. */
  align-content:start;
  max-height:calc(100% - var(--headerCeiling));
  opacity:.55;
  /* Transparency fade — fully visible at top, fading to 0 by the bottom */
  mask-image:linear-gradient(180deg,#000 0%,#000 38%,rgba(0,0,0,.55) 70%,rgba(0,0,0,0) 100%);
  -webkit-mask-image:linear-gradient(180deg,#000 0%,#000 38%,rgba(0,0,0,.55) 70%,rgba(0,0,0,0) 100%);
}
/* Hide internal sections that are unsafe / interactive in the peek
   (description rich-text editor, picker menu pop-overs). They show on
   expand. The static section blocks (meta strip, dates grid, recurring
   panel, meeting card, people grid headers) remain visible as the peek. */
.density-180min .open-body .description-editor,
.density-360min .open-body .description-editor,
.density-600min .open-body .description-editor,
.density-180min .open-body .responsible-menu,
.density-360min .open-body .responsible-menu,
.density-600min .open-body .responsible-menu,
.density-180min .open-body .observer-menu,
.density-360min .open-body .observer-menu,
.density-600min .open-body .observer-menu{ display:none }
/* —————— Expanded state (chevron clicked) —————— */
.card.is-expanded.density-15min,.card.is-expanded.density-30min,.card.is-expanded.density-60min,
.card.is-expanded.density-180min,.card.is-expanded.density-360min,.card.is-expanded.density-600min{
  height:auto !important;min-height:0;max-height:none;
  z-index:600;overflow:visible;
}
.card.is-expanded .open-body{
  position:relative;display:grid;pointer-events:auto;
  mask-image:none;-webkit-mask-image:none;opacity:1;overflow:visible;
}
.card.is-expanded .open-body .description-editor,
.card.is-expanded .open-body .responsible-menu,
.card.is-expanded .open-body .observer-menu{ display:block }
.card.is-expanded .open-body .description-editor{ display:flex; flex-direction:column; }
.card.is-expanded .main{height:var(--headerCeiling);max-height:var(--headerCeiling)}
.card.is-expanded .card-toggle .icon{transform:rotate(180deg)}
/* When expanded, every density inherits the canonical (60min) full header
   so the user always sees the standard title, meta, and controls. The body
   is rendered cleanly BELOW the header — no mask, no overlap, no clip. */
.card.is-expanded.density-15min,
.card.is-expanded.density-30min{
  --headerCeiling:120px;--cardPad:18px;--titleSize:15px;--tokenH:26px;--statusH:30px;--statusW:102px;--statusRadius:999px;
}
.card.is-expanded.density-15min .main,
.card.is-expanded.density-30min .main{
  grid-template-columns:34px 4px minmax(0,1fr);gap:14px;padding:14px;
  height:auto !important;max-height:none !important;min-height:120px;
}
.card.is-expanded.density-15min .grip,
.card.is-expanded.density-30min .grip{min-height:90px;border-radius:12px;box-shadow:0 8px 18px var(--shadow)}
.card.is-expanded.density-15min .grip svg,
.card.is-expanded.density-30min .grip svg{width:14px;height:14px}
.card.is-expanded.density-15min .header-content,
.card.is-expanded.density-30min .header-content{display:grid !important;flex-direction:column;gap:10px;height:auto;align-content:start}
.card.is-expanded.density-15min .title-row,
.card.is-expanded.density-30min .title-row{margin-right:34px;gap:6px;flex:initial;min-width:0}
.card.is-expanded.density-15min .title-input,
.card.is-expanded.density-30min .title-input{font-size:15px;line-height:1.16;white-space:normal;overflow:visible;text-overflow:clip;font-weight:950}
.card.is-expanded.density-15min .header-meta,
.card.is-expanded.density-30min .header-meta{display:flex;flex-wrap:wrap;gap:10px;font-size:11.5px;line-height:1.2}
.card.is-expanded.density-15min .header-meta > span:first-child,
.card.is-expanded.density-30min .header-meta > span:first-child{display:inline-flex}
.card.is-expanded.density-15min .header-meta .icon,
.card.is-expanded.density-30min .header-meta .icon{width:13px;height:13px}
.card.is-expanded.density-15min .actions,
.card.is-expanded.density-30min .actions{display:grid;grid-template-columns:minmax(160px,1fr) auto auto;flex-direction:initial;gap:9px;align-items:start;width:100%}
.card.is-expanded.density-15min .workarea-stack,
.card.is-expanded.density-30min .workarea-stack{display:flex;gap:7px;align-items:center;flex:initial;min-width:0}
.card.is-expanded.density-15min .pe-stack,
.card.is-expanded.density-30min .pe-stack{display:grid;grid-template-columns:auto auto;gap:9px;flex:initial;min-width:0}
.card.is-expanded.density-15min .token-select,
.card.is-expanded.density-30min .token-select{height:26px;padding:0 9px;font-size:12px;gap:7px;border-width:1.5px;min-width:0}
.card.is-expanded.density-15min .token-select .icon,
.card.is-expanded.density-30min .token-select .icon{display:inline-flex;width:12px;height:12px}
.card.is-expanded.density-15min .token-dot,
.card.is-expanded.density-30min .token-dot{width:8px;height:8px;box-shadow:0 0 0 3px color-mix(in srgb,var(--token-color) 16%,transparent)}
.card.is-expanded.density-15min .token-select select,
.card.is-expanded.density-30min .token-select select{min-width:72px;max-width:132px;padding-right:12px;font-size:12px}
.card.is-expanded.density-15min .status,
.card.is-expanded.density-30min .status{height:30px;min-width:102px;padding:0 12px;font-size:12px;border-radius:999px;gap:7px}
.card.is-expanded.density-15min .status-zone,
.card.is-expanded.density-30min .status-zone{display:grid !important;flex-direction:initial;gap:4px;justify-items:end;align-items:initial}
.card.is-expanded.density-15min .card-toggle,
.card.is-expanded.density-30min .card-toggle{height:22px;width:42px;border-radius:11px;justify-self:end}
.card.is-expanded.density-15min .edit,
.card.is-expanded.density-30min .edit{display:grid !important;width:26px;height:26px;top:10px;right:10px}
/* 60/180/360/600 already render at canonical header height; restore the
   status-zone + actions grids so they sit beside the chevron normally. */
.card.is-expanded.density-60min .status-zone,
.card.is-expanded.density-180min .status-zone,
.card.is-expanded.density-360min .status-zone,
.card.is-expanded.density-600min .status-zone{
  display:grid !important;flex-direction:initial;gap:4px;justify-items:end;align-items:initial;
}
.card.is-expanded.density-180min .actions,
.card.is-expanded.density-360min .actions,
.card.is-expanded.density-600min .actions{
  grid-template-columns:minmax(160px,1fr) auto auto;
  grid-template-areas:none;
  row-gap:0;
}
.card.is-expanded.density-180min .workarea-stack,
.card.is-expanded.density-360min .workarea-stack,
.card.is-expanded.density-600min .workarea-stack,
.card.is-expanded.density-180min .pe-stack,
.card.is-expanded.density-360min .pe-stack,
.card.is-expanded.density-600min .pe-stack{grid-area:auto}
.card.is-expanded.density-180min .status-zone .status,
.card.is-expanded.density-360min .status-zone .status,
.card.is-expanded.density-600min .status-zone .status{
  grid-area:auto;height:30px;
}
.card.is-expanded.density-180min .status-zone .card-toggle,
.card.is-expanded.density-360min .status-zone .card-toggle,
.card.is-expanded.density-600min .status-zone .card-toggle{
  grid-area:auto;justify-self:end;width:42px;height:22px;border-radius:11px;
}
.card.is-expanded.density-60min .status-zone .status,
.card.is-expanded.density-60min .status-zone .card-toggle{align-self:initial}
.card.is-expanded.density-60min .card-toggle{width:42px;height:22px;border-radius:11px}
/* The expanded body always sits cleanly BELOW the header — no overlap. */
.card.is-expanded .open-body{
  position:relative !important;
  margin-top:0;z-index:1;
  border-top:1px solid var(--border);
  padding:14px 18px 18px;gap:12px;
  background:color-mix(in srgb,var(--surface2) 62%,transparent);
}
/* When expanded, dim siblings underneath via the rail's existing wrapper
   (consumers of the card add .is-expanded to the wrapper too if needed). */

/* —————— Mobile mirror —————— */
.mobile-card.density-15min,.mobile-card.density-30min,.mobile-card.density-60min,
.mobile-card.density-180min,.mobile-card.density-360min,.mobile-card.density-600min{
  min-height:60px;height:100%;
}
.mobile-card.density-15min .mobile-controls,
.mobile-card.density-30min .mobile-controls,
.mobile-card.density-15min .open-body,
.mobile-card.density-30min .open-body{ display:none }
/* Squashed layout for narrow widths within an effort density */
.mode-squashed.density-15min,.mode-squashed.density-30min,.mode-squashed.density-60min,
.mode-squashed.density-180min,.mode-squashed.density-360min,.mode-squashed.density-600min{
  --titleSize:13px;--tokenH:22px;--statusH:26px;
}
.mode-squashed.density-15min .header-meta,.mode-squashed.density-30min .header-meta,
.mode-squashed.density-60min .header-meta,.mode-squashed.density-180min .header-meta,
.mode-squashed.density-360min .header-meta,.mode-squashed.density-600min .header-meta{
  font-size:9.5px;
}
/* —————— Spec overrides (May 2026) ——————
   • 15min: drop the time chip on squashed widths (no room).
   • 30min + 60min: status-zone becomes inline row; status & chevron same height.
   • 180/360/600: chevron drops to its own row below wa/p/e/status (display:contents on status-zone). */
.mode-squashed.density-15min .header-meta{display:none}
/* 30min — flatten status-zone into a row; chevron matches status height. */
.density-30min .status-zone{display:inline-flex;flex-direction:row;align-items:center;gap:5px}
.density-30min .status-zone .status,.density-30min .status-zone .card-toggle{height:22px;align-self:center}
.density-30min .card-toggle{width:30px}
/* 60min — same: row layout, both at 30px height for visual parity. */
.density-60min .status-zone{display:inline-flex;flex-direction:row;align-items:center;gap:6px}
.density-60min .status-zone .status,.density-60min .status-zone .card-toggle{height:30px;align-self:center}
.density-60min .card-toggle{width:42px;border-radius:999px}
/* 180/360/600 — chevron on its own line below actions row. Status-zone uses
   display:contents so .status and .card-toggle become direct grid children of
   .actions, which is now a 2-row grid. */
.density-180min .actions,.density-360min .actions,.density-600min .actions{
  display:grid;
  grid-template-columns:minmax(0,1fr) minmax(0,auto) minmax(0,auto);
  grid-template-areas:
    "wa pe status"
    "chev chev chev";
  row-gap:8px;column-gap:6px;align-items:center;
}
.density-180min .workarea-stack,.density-360min .workarea-stack,.density-600min .workarea-stack{grid-area:wa}
.density-180min .pe-stack,.density-360min .pe-stack,.density-600min .pe-stack{grid-area:pe}
.density-180min .status-zone,.density-360min .status-zone,.density-600min .status-zone{display:contents}
.density-180min .status-zone .status,.density-360min .status-zone .status,.density-600min .status-zone .status{grid-area:status;height:30px}
.density-180min .status-zone .card-toggle,.density-360min .status-zone .card-toggle,.density-600min .status-zone .card-toggle{grid-area:chev;justify-self:center;width:120px;height:24px;border-radius:999px}
/* —————— MOBILE rework — per density (creative truncation) ——————
   The .mobile-card path is engaged when responsive width is in 'mobile'
   mode (<470 px). Each density gets a tailored layout that prioritises
   reachability + scannability on a phone. */
.mobile-card{font-size:13px}
.mobile-card .edit{top:6px;right:6px;width:24px;height:24px;border-radius:8px}
/* 15min mobile — drop EVERYTHING except title + status. Single 38px-tall row. */
.mobile-card.density-15min{min-height:38px;height:38px}
.mobile-card.density-15min .mobile-top{grid-template-columns:18px 3px minmax(0,1fr);gap:6px;padding:5px 8px}
.mobile-card.density-15min .mobile-grip{height:24px;border-radius:7px}
.mobile-card.density-15min .mobile-grip svg{width:10px;height:10px}
.mobile-card.density-15min .mobile-title-wrap .title-row{margin-right:0}
.mobile-card.density-15min .mobile-title-wrap .title-input{font-size:12px;line-height:1}
.mobile-card.density-15min .mobile-title-wrap .header-meta{display:none}
.mobile-card.density-15min .mobile-controls{display:flex;flex-direction:row;align-items:center;justify-content:flex-end;padding:0 6px 0 0;gap:5px;background:transparent;position:absolute;right:34px;top:6px;height:26px}
.mobile-card.density-15min .mobile-controls .token-select{display:none}     /* chips don't fit — opens via expand */
.mobile-card.density-15min .mobile-controls .mobile-status{height:24px;min-width:60px;padding:0 8px;font-size:10px;border-radius:999px}
.mobile-card.density-15min .mobile-controls .card-toggle{height:24px;width:24px;border-radius:7px}
.mobile-card.density-15min .edit{top:6px;right:6px;width:0;height:0;display:none}
/* 30min mobile — title row + tight chips row */
.mobile-card.density-30min{min-height:74px;height:auto}
.mobile-card.density-30min .mobile-top{grid-template-columns:24px 3px minmax(0,1fr);gap:7px;padding:6px 10px 4px}
.mobile-card.density-30min .mobile-grip{height:30px}
.mobile-card.density-30min .mobile-title-wrap .title-input{font-size:13px}
.mobile-card.density-30min .mobile-title-wrap .header-meta{display:none}
.mobile-card.density-30min .mobile-controls{display:grid;grid-template-columns:auto auto auto auto auto;gap:5px;padding:2px 10px 8px;align-items:center;justify-content:start}
.mobile-card.density-30min .mobile-controls .token-select{height:24px;padding:0 7px;font-size:10.5px;gap:4px}
.mobile-card.density-30min .mobile-controls .token-select .icon{display:none}
.mobile-card.density-30min .mobile-controls .token-select select{max-width:60px;font-weight:900}
.mobile-card.density-30min .mobile-controls .mobile-status{height:24px;padding:0 9px;font-size:10.5px;border-radius:999px;min-width:0}
.mobile-card.density-30min .mobile-controls .status-zone{display:flex;flex-direction:row;gap:5px;align-items:center;justify-content:flex-end}
.mobile-card.density-30min .mobile-controls .card-toggle{height:24px;width:30px;border-radius:7px}
/* 60min mobile — title + compact time + 2-row chips */
.mobile-card.density-60min{min-height:120px;height:auto}
.mobile-card.density-60min .mobile-top{grid-template-columns:28px 3px minmax(0,1fr);gap:8px;padding:8px 10px 4px}
.mobile-card.density-60min .mobile-title-wrap .header-meta{font-size:10.5px;gap:6px}
.mobile-card.density-60min .mobile-title-wrap .header-meta > span:first-child{display:none}    /* drop date — keep time only */
.mobile-card.density-60min .mobile-title-wrap .header-date-picker{display:none}
.mobile-card.density-60min .mobile-controls{padding:4px 10px 10px;gap:6px;grid-template-columns:1fr 1fr 1fr}
.mobile-card.density-60min .mobile-controls .token-select{height:26px;font-size:10.5px;justify-content:center}
.mobile-card.density-60min .mobile-controls .token-select .icon{display:none}
.mobile-card.density-60min .mobile-controls .mobile-workarea{grid-column:1/-1}
.mobile-card.density-60min .mobile-controls .status-zone{grid-column:1/-1;display:grid;grid-template-columns:1fr auto;gap:6px;align-items:center}
.mobile-card.density-60min .mobile-controls .mobile-status{height:30px;border-radius:999px}
.mobile-card.density-60min .mobile-controls .card-toggle{height:30px;width:48px;border-radius:999px}
/* 180/360/600 mobile — same compact header as 60min; chevron is full-width below */
.mobile-card.density-180min,.mobile-card.density-360min,.mobile-card.density-600min{min-height:120px;height:100%}
.mobile-card.density-180min .mobile-top,.mobile-card.density-360min .mobile-top,.mobile-card.density-600min .mobile-top{grid-template-columns:28px 3px minmax(0,1fr);gap:8px;padding:8px 10px 4px}
.mobile-card.density-180min .mobile-title-wrap .header-meta > span:first-child,
.mobile-card.density-360min .mobile-title-wrap .header-meta > span:first-child,
.mobile-card.density-600min .mobile-title-wrap .header-meta > span:first-child{display:none}
.mobile-card.density-180min .mobile-controls,.mobile-card.density-360min .mobile-controls,.mobile-card.density-600min .mobile-controls{
  padding:4px 10px 10px;gap:6px;
  display:grid;grid-template-columns:1fr 1fr 1fr;
  grid-template-areas:"wa wa wa" "p e status" "chev chev chev";
}
.mobile-card.density-180min .mobile-controls .mobile-workarea,
.mobile-card.density-360min .mobile-controls .mobile-workarea,
.mobile-card.density-600min .mobile-controls .mobile-workarea{grid-area:wa}
.mobile-card.density-180min .mobile-controls > .token-select.priority-token,
.mobile-card.density-360min .mobile-controls > .token-select.priority-token,
.mobile-card.density-600min .mobile-controls > .token-select.priority-token{grid-area:p}
.mobile-card.density-180min .mobile-controls > .token-select.effort-token,
.mobile-card.density-360min .mobile-controls > .token-select.effort-token,
.mobile-card.density-600min .mobile-controls > .token-select.effort-token{grid-area:e}
.mobile-card.density-180min .mobile-controls .status-zone,
.mobile-card.density-360min .mobile-controls .status-zone,
.mobile-card.density-600min .mobile-controls .status-zone{display:contents}
.mobile-card.density-180min .mobile-controls .mobile-status,
.mobile-card.density-360min .mobile-controls .mobile-status,
.mobile-card.density-600min .mobile-controls .mobile-status{grid-area:status;height:28px;border-radius:999px;margin-left:auto;min-width:90px}
.mobile-card.density-180min .mobile-controls .status-zone .card-toggle,
.mobile-card.density-360min .mobile-controls .status-zone .card-toggle,
.mobile-card.density-600min .mobile-controls .status-zone .card-toggle{
  /* Promote the chevron out of status-zone into the full-width row below.
     status-zone uses display:contents above so the chevron becomes a direct
     grandchild of mobile-controls — assignable to the chev grid area. */
  grid-area:chev;justify-self:stretch;width:100%;height:30px;border-radius:999px;
  margin-top:2px;
}
.mobile-card.density-180min .mobile-controls .status-zone,
.mobile-card.density-360min .mobile-controls .status-zone,
.mobile-card.density-600min .mobile-controls .status-zone{display:contents}
.mobile-card.density-180min .mobile-status{height:28px;border-radius:999px}
.mobile-card.density-360min .mobile-status{height:28px;border-radius:999px}
.mobile-card.density-600min .mobile-status{height:28px;border-radius:999px}
/* Floating status morph (portal — escapes card overflow clipping) */
.floating-status-morph{border-radius:14px;border:1px solid var(--border);background:var(--surface);overflow:hidden}
.floating-status-morph .status-grid{grid-template-columns:1fr 1fr;gap:1px;background:var(--border);padding:1px}
.floating-status-morph .status-choice{min-height:56px;padding:10px 12px;gap:8px}
.floating-status-morph .status-label{font-size:12px}
.floating-status-morph .status-morph-close{top:4px;right:4px;height:20px;padding:0 7px;font-size:10px;background:color-mix(in srgb,var(--surface) 86%,transparent);border:1px solid var(--border);border-radius:7px;color:var(--text3)}
/* —————— Spec overrides (May 2026 — Round 2) ——————
   • 15min regular: show time next to title; work area full-size of selected text
   • 30min regular: time next to title; work area full-size of selected text
   • 30/60/180/360/600: move chevron OUT of actions grid, absolutely-position
     it next to the pencil icon at matching size. */

/* === 15min — time inline with title; work area full size === */
.density-15min .header-meta{display:inline-flex !important;flex:0 0 auto;gap:0;font-size:10px;line-height:1;align-self:center;margin-left:4px}
.density-15min .header-meta > span:first-child{display:none}    /* still hide date */
.density-15min .header-meta > span:nth-child(2){display:inline-flex;gap:4px;align-items:center;color:var(--text3);font-weight:850}
.density-15min .header-meta .icon{width:10px;height:10px}
/* Restructure header-content so order is: title — time — actions(workarea+pe+status) */
.density-15min .header-content{display:flex !important;flex-direction:row;align-items:center;gap:8px;height:100%;min-width:0;flex-wrap:nowrap}
.density-15min .title-row{flex:0 1 auto;min-width:60px;max-width:55%}
.density-15min .header-controls{flex:1 1 auto;min-width:0;display:flex;justify-content:flex-end}
/* Work area full-size to selected text on regular widths */
.density-15min .workarea-token select{max-width:none;min-width:0;width:auto;padding-right:8px}
.density-15min .workarea-token{padding:0 7px;height:20px;flex:0 0 auto}
.density-15min .effort-token select{max-width:none;min-width:0;width:auto;padding-right:8px}
.density-15min .priority-token select{max-width:none;min-width:0;width:auto;padding-right:8px}
.mode-squashed.density-15min .workarea-token select{max-width:60px}
.mode-squashed.density-15min .effort-token select{max-width:38px}

/* === 30min — time inline with title; work area full size === */
.density-30min .header-content{display:grid !important;grid-template-areas:"title meta" "actions actions";grid-template-columns:minmax(0,1fr) auto;gap:3px 8px;align-items:center;height:100%;align-content:center}
.density-30min .title-row{grid-area:title;min-width:0;margin-right:0}
.density-30min .header-meta{grid-area:meta;display:inline-flex !important;gap:5px;font-size:10px;line-height:1;color:var(--text3);font-weight:850;align-items:center}
.density-30min .header-meta > span:first-child{display:none}    /* hide date — keep only time */
.density-30min .header-meta > span:nth-child(2){display:inline-flex;gap:4px;align-items:center}
.density-30min .header-meta .icon{width:11px;height:11px}
.density-30min .header-controls{grid-area:actions;min-width:0}
/* Work area full-size to selected text on regular widths */
.density-30min .workarea-token select{max-width:none;min-width:0;width:auto;padding-right:10px}
.density-30min .workarea-token{padding:0 8px}
.density-30min .effort-token select{max-width:none;min-width:0;width:auto;padding-right:10px}
.density-30min .priority-token select{max-width:none;min-width:0;width:auto;padding-right:10px}
.mode-squashed.density-30min .workarea-token select{max-width:80px}
.mode-squashed.density-30min .effort-token select{max-width:48px}

/* === Chevron next to pencil — 30/60/180/360/600 === */
/* Pull chevron OUT of the .actions grid by absolute positioning. */
.density-30min .card-toggle,
.density-60min .card-toggle,
.density-180min .card-toggle,
.density-360min .card-toggle,
.density-600min .card-toggle{
  position:absolute;z-index:4;justify-self:auto;
}
/* Drop the chevron column from the actions grids (it's now absolute). */
.density-30min .actions{grid-template-columns:minmax(0,1fr) auto auto !important}
.density-60min .status-zone{display:grid !important;flex-direction:initial;gap:4px;justify-items:end}
.density-60min .status-zone .card-toggle{align-self:auto}
.density-180min .actions,.density-360min .actions,.density-600min .actions{
  grid-template-areas:"wa pe status" !important;
  grid-template-columns:minmax(0,1fr) minmax(0,auto) minmax(0,auto) !important;
  row-gap:0 !important;
}
.density-180min .status-zone .card-toggle,
.density-360min .status-zone .card-toggle,
.density-600min .status-zone .card-toggle{grid-area:auto}
/* 30min — chevron at edit's right (edit: top:4 right:4 width:18) */
.density-30min .card-toggle{top:4px;right:26px;width:18px;height:18px;border-radius:6px;padding:0}
.density-30min .card-toggle .icon{width:10px;height:10px}
/* 60/180/360/600 — chevron at canonical edit's right (edit: top:12 right:12 width:28) */
.density-60min .card-toggle,
.density-180min .card-toggle,
.density-360min .card-toggle,
.density-600min .card-toggle{
  top:12px;right:46px;width:28px;height:28px;border-radius:10px;padding:0;
}
.density-60min .card-toggle .icon,
.density-180min .card-toggle .icon,
.density-360min .card-toggle .icon,
.density-600min .card-toggle .icon{width:13px;height:13px}
/* Squashed — pencil shrinks to 26px so chevron follows. */
.mode-squashed.density-60min .card-toggle,
.mode-squashed.density-180min .card-toggle,
.mode-squashed.density-360min .card-toggle,
.mode-squashed.density-600min .card-toggle{top:10px;right:42px;width:26px;height:26px;border-radius:9px}
/* Make sure cards aren't clipping the absolute chevron. */
.density-30min,.density-60min,.density-180min,.density-360min,.density-600min{overflow:hidden}
/* When expanded, chevron stays next to pencil at canonical 28px. */
.card.is-expanded.density-15min .card-toggle,
.card.is-expanded.density-30min .card-toggle,
.card.is-expanded.density-60min .card-toggle,
.card.is-expanded.density-180min .card-toggle,
.card.is-expanded.density-360min .card-toggle,
.card.is-expanded.density-600min .card-toggle{
  position:absolute;top:12px;right:46px;width:28px;height:28px;border-radius:10px;justify-self:auto;z-index:4;
}
.card.is-expanded.density-15min .edit,
.card.is-expanded.density-30min .edit{
  display:grid !important;
  /* Reset to canonical 28×28 top-right so it doesn't overlap the
     expanded chevron sitting at right:46 */
  top:12px !important;right:12px !important;
  width:28px !important;height:28px !important;border-radius:10px !important;
}
.card.is-expanded.density-15min .edit .icon,
.card.is-expanded.density-30min .edit .icon{width:13px !important;height:13px !important}
/* For expanded 15/30, also force chevron to canonical 28×28 so the pair
   doesn't overlap and matches 60+ density expanded state. */
.card.is-expanded.density-15min .card-toggle,
.card.is-expanded.density-30min .card-toggle,
.card.is-expanded.density-15min .status-zone .card-toggle,
.card.is-expanded.density-30min .status-zone .card-toggle{
  top:12px !important;right:46px !important;
  width:28px !important;height:28px !important;border-radius:10px !important;
}
.card.is-expanded.density-15min .card-toggle .icon,
.card.is-expanded.density-30min .card-toggle .icon{width:13px !important;height:13px !important}

/* ═══════════════════ Spec overrides — May 2026 — Round 3 ═══════════════════
   • 15min  : pencil visible at same size as chevron, both absolute right;
              workarea after time on the LEFT; PE/Status on the RIGHT;
              time picker chevron added.
   • 30min  : date AND time (with pickers) inline next to title;
              chevron next to pencil (already absolute) at matching size.
   • 60/180/360/600 : ensure chevron matches pencil size exactly. */

/* ── Time picker chrome (mirrors .date-picker-wrap) ── */
.time-picker-wrap{display:inline-flex;align-items:center;gap:2px}
.time-text{display:inline-flex;align-items:center}
.time-chevron-input-wrap{position:relative;display:grid;place-items:center;width:18px;height:18px;cursor:pointer}
.time-chevron{width:12px;height:12px;color:var(--text3);opacity:.7;pointer-events:none}
.time-chevron-input-wrap:hover .time-chevron{opacity:1}
.time-arrow-input{position:absolute;inset:0;width:18px;height:18px;opacity:0;cursor:pointer;color:transparent;background:transparent;border:0}
.time-arrow-input::-webkit-calendar-picker-indicator{position:absolute;inset:0;width:18px;height:18px;opacity:0;cursor:pointer}
/* On the 15min density chrome, scale these down. */
.density-15min .time-chevron-input-wrap{width:14px;height:14px}
.density-15min .time-arrow-input{width:14px;height:14px}
.density-15min .time-arrow-input::-webkit-calendar-picker-indicator{width:14px;height:14px}
.density-15min .time-chevron{width:10px;height:10px}

/* ═════ 15min: pencil + chevron same-size, TOP-RIGHT corner ═════ */
.density-15min .edit{
  display:grid !important;
  position:absolute;top:5px;right:5px;
  width:20px;height:20px;border-radius:6px;
  border:1px solid var(--border);
  background:color-mix(in srgb,var(--surface) 86%,transparent);
  color:var(--text3);place-items:center;cursor:pointer;opacity:.84;z-index:4;
}
.density-15min .edit .icon{width:11px;height:11px}
.density-15min .card-toggle{
  position:absolute !important;top:5px !important;right:30px !important;
  width:20px !important;height:20px !important;border-radius:6px !important;
  justify-self:auto !important;display:grid !important;place-items:center !important;z-index:4 !important;padding:0 !important;
  border:1px solid color-mix(in srgb,var(--border) 72%,transparent) !important;
  background:color-mix(in srgb,var(--surface2) 82%,transparent) !important;
  color:var(--text3) !important;cursor:pointer !important;
}
.density-15min .card-toggle .icon{width:11px !important;height:11px !important}
.density-15min .status-zone .card-toggle{
  position:absolute !important;top:5px !important;right:30px !important;
  width:20px !important;height:20px !important;border-radius:6px !important;
  display:grid !important;
}
/* Reserve space for the absolute pair so content doesn't overlap. */
.density-15min .header-content{padding-right:58px}

/* ═════ 15min content order: Title — Time — Workarea ═════ */
/* Show date + time meta inline (date currently hidden — keep hiding date to
   save space; show ONLY time inline next to title). User wants the time-
   picker arrow on the time. */
.density-15min .header-meta{display:inline-flex !important;flex:0 0 auto;gap:0;font-size:10px;line-height:1;align-items:center}
.density-15min .header-meta > span:first-child{display:none}        /* hide date — too tight */
.density-15min .header-meta > span:nth-child(2){display:inline-flex;gap:4px;align-items:center}
.density-15min .header-meta .icon{width:10px;height:10px}
.density-15min .time-text{font-size:10px;font-weight:850}
/* Use flex order to put Workarea (in .actions) AFTER the time meta. */
.density-15min .header-content{display:flex !important;flex-direction:row;align-items:center;gap:8px;height:100%;min-width:0;flex-wrap:nowrap}
.density-15min .title-row{order:1;flex:0 1 auto;min-width:60px;max-width:50%}
.density-15min .header-meta{order:2}
.density-15min .header-controls{order:3;flex:1 1 auto;min-width:0;display:flex;align-items:center;gap:6px;justify-content:flex-end}
.density-15min .header-controls .actions{display:flex !important;flex-direction:row;align-items:center;gap:6px;width:100%;flex-wrap:nowrap}
/* Inside actions: workarea pushed left, PE/Status pushed right. */
.density-15min .actions .workarea-stack{order:1;flex:0 0 auto;margin-right:auto}
.density-15min .actions .pe-stack{order:2;flex:0 0 auto}
.density-15min .actions .status-zone{order:3;flex:0 0 auto;display:flex !important;align-items:center;gap:4px}
/* Status-zone holds the chevron; we promote it OUT to absolute via the
   later .density-15min .status-zone .card-toggle{position:absolute…} rule. */

/* ═════ 30min: date + time inline next to title; pencil + chevron same size ═════ */
/* Restore .header-meta visibility and place inline with title-row. */
.density-30min .header-content{
  display:flex !important;flex-wrap:wrap;align-items:center;
  gap:6px 10px;height:100%;align-content:center;
  padding-right:58px;     /* reserve space for absolute chevron + pencil */
  grid-template-areas:none !important;grid-template-columns:none !important;
}
.density-30min .title-row{order:1;flex:0 1 auto;max-width:50%;margin-right:0;min-width:0}
.density-30min .header-meta{
  order:2;flex:0 1 auto;display:inline-flex !important;
  gap:8px;font-size:10px;line-height:1;align-items:center;
}
.density-30min .header-meta > span{display:inline-flex;gap:4px;align-items:center;color:var(--text3);font-weight:850}
.density-30min .header-meta > span:first-child{display:inline-flex !important}   /* SHOW date */
.density-30min .header-meta .icon{width:11px;height:11px}
.density-30min .header-date-picker{width:74px;font-size:10px}
.density-30min .time-text{font-size:10px}
.density-30min .header-controls{order:3;flex:1 0 100%;min-width:0}
.density-30min .header-controls .actions{display:grid;grid-template-columns:minmax(0,1fr) auto auto !important;gap:5px;align-items:center;width:100%}
/* 30min absolute chevron + pencil at matching size (18×18). TOP-RIGHT corner. */
.density-30min .edit{
  position:absolute;top:5px;right:5px;
  width:18px;height:18px;border-radius:6px;display:grid;place-items:center;
}
.density-30min .edit .icon{width:10px;height:10px}
.density-30min .card-toggle{
  position:absolute !important;top:5px !important;right:28px !important;
  width:18px !important;height:18px !important;border-radius:6px !important;padding:0 !important;
  justify-self:auto !important;display:grid !important;place-items:center !important;z-index:4 !important;
  border:1px solid color-mix(in srgb,var(--border) 72%,transparent) !important;
  background:color-mix(in srgb,var(--surface2) 82%,transparent) !important;
  color:var(--text3) !important;cursor:pointer !important;
}
.density-30min .card-toggle .icon{width:10px !important;height:10px !important}
.density-30min .status-zone .card-toggle{
  position:absolute !important;top:5px !important;right:28px !important;
  width:18px !important;height:18px !important;border-radius:6px !important;padding:0 !important;
  justify-self:auto !important;display:grid !important;
}

/* ═════ 60/180/360/600: chevron strictly matches pencil size ═════ */
/* Canonical .edit is 28×28 border-radius 10px. Force the chevron to match. */
.density-60min .edit,
.density-180min .edit,
.density-360min .edit,
.density-600min .edit{
  width:28px;height:28px;border-radius:10px;top:12px;right:12px;
}
.density-60min .edit .icon,
.density-180min .edit .icon,
.density-360min .edit .icon,
.density-600min .edit .icon{width:13px;height:13px}
.density-60min .status-zone .card-toggle,
.density-180min .status-zone .card-toggle,
.density-360min .status-zone .card-toggle,
.density-600min .status-zone .card-toggle{
  position:absolute !important;
  top:12px !important;right:46px !important;     /* edit is at right:12 width:28 → gap 6 */
  width:28px !important;height:28px !important;border-radius:10px !important;
  justify-self:auto !important;padding:0 !important;z-index:4 !important;
  display:grid !important;place-items:center !important;
  border:1px solid color-mix(in srgb,var(--border) 72%,transparent) !important;
  background:color-mix(in srgb,var(--surface2) 82%,transparent) !important;
  color:var(--text3) !important;opacity:.84 !important;cursor:pointer !important;
}
.density-60min .status-zone .card-toggle .icon,
.density-180min .status-zone .card-toggle .icon,
.density-360min .status-zone .card-toggle .icon,
.density-600min .status-zone .card-toggle .icon{width:13px !important;height:13px !important}
/* Squashed mirrors */
.mode-squashed.density-60min .status-zone .card-toggle,
.mode-squashed.density-180min .status-zone .card-toggle,
.mode-squashed.density-360min .status-zone .card-toggle,
.mode-squashed.density-600min .status-zone .card-toggle{
  top:10px !important;right:42px !important;width:26px !important;height:26px !important;border-radius:9px !important;
}
.mode-squashed.density-60min .edit,
.mode-squashed.density-180min .edit,
.mode-squashed.density-360min .edit,
.mode-squashed.density-600min .edit{top:10px;right:10px;width:26px;height:26px;border-radius:9px}

/* ═════ V3 Base — standard / calm densities: chevron next to pencil top-right ═════
   Same canonical 28×28 placement as the 60+ min effort tiers so every V3 card
   in the design system has the pencil + expand chevron pair top-right. */
.density-standard .status-zone .card-toggle,
.density-calm .status-zone .card-toggle{
  position:absolute !important;
  top:12px !important;right:46px !important;     /* edit at right:12 width:28 → gap 6 */
  width:28px !important;height:28px !important;border-radius:10px !important;
  justify-self:auto !important;padding:0 !important;z-index:4 !important;
  display:grid !important;place-items:center !important;
  border:1px solid color-mix(in srgb,var(--border) 72%,transparent) !important;
  background:color-mix(in srgb,var(--surface2) 82%,transparent) !important;
  color:var(--text3) !important;opacity:.84 !important;cursor:pointer !important;
}
.density-standard .status-zone .card-toggle .icon,
.density-calm .status-zone .card-toggle .icon{width:13px !important;height:13px !important}
/* Squashed mirror — pencil shrinks to 26×26 so chevron follows. */
/* ═════ Work area dropdown — full-size to text content (Round 4) ═════
   Every density's workarea token must size to the longest visible label and
   never apply uppercase. Effort/priority tokens are NOT included — those keep
   their compact widths because they're a single-letter chip. */
.token-select.workarea-token{max-width:none !important;width:auto !important;text-transform:none !important;font-variant-caps:normal !important}
.token-select.workarea-token select{
  width:auto !important;min-width:0 !important;max-width:none !important;
  text-transform:none !important;
  padding-right:14px !important;
}
.token-select.workarea-token option{text-transform:none !important}
/* Same for the OrkaTaskCard (V4 legacy field-groups) wrap. */
.oth-workarea-select-wrap, .oth-workarea-select { text-transform: none !important; }
`;

const SCOPE = "orka-v3-scope";

/** Runtime CSS scoper — prefixes every selector (including inside @media) with `.orka-v3-scope ` */
function scopeCss(css, scope) {
  const prefixSelectors = (sel) =>
    sel.split(",").map((s) => {
      const t = s.trim();
      if (!t) return s;
      if (t.startsWith(".") + "" === "true" && t.slice(1).split(/[\s.[:>+~]/)[0] === scope) return t; // already scoped
      if (t === "*" || t.match(/^button,|button$|^input,|input$|^select,|select$|^textarea,|textarea$/)) {
        return `.${scope} ${t}`;
      }
      return `.${scope} ${t}`;
    }).join(", ");

  const out = [];
  let i = 0;
  while (i < css.length) {
    if (css.startsWith("@media", i)) {
      const open = css.indexOf("{", i);
      let depth = 1; let j = open + 1;
      while (j < css.length && depth > 0) {
        if (css[j] === "{") depth++;
        else if (css[j] === "}") depth--;
        j++;
      }
      const inner = css.slice(open + 1, j - 1);
      out.push(css.slice(i, open + 1));
      out.push(scopeCss(inner, scope));
      out.push("}");
      i = j;
      continue;
    }
    const next = css.indexOf("{", i);
    if (next === -1) { out.push(css.slice(i)); break; }
    const close = css.indexOf("}", next);
    const selectors = css.slice(i, next);
    const body = css.slice(next + 1, close);
    out.push(prefixSelectors(selectors));
    out.push("{");
    out.push(body);
    out.push("}");
    i = close + 1;
  }
  return out.join("");
}

let __cssInjected = false;
export function useV3TaskCardPreviewStyles() {
  useEffect(() => {
    if (__cssInjected) return;
    // Remove any prior style tag (HMR resilience)
    document.querySelectorAll('style[data-orka-v3-scoped="true"]').forEach((el) => el.remove());
    const style = document.createElement("style");
    style.setAttribute("data-orka-v3-scoped", "true");
    // Scoped user CSS + an un-scoped block that seeds --surface/--text/etc. on
    // the scope root itself (so the card works outside `.panel[data-theme]`).
    const extra = `
.orka-v3-scope,
.orka-v3-scope[data-theme="light"]{
  --surface:#fff;--surface2:#f8fafc;--border:rgba(15,23,42,.14);
  --text:#07111f;--text2:#334155;--text3:#64748b;
  --shadow:rgba(15,23,42,.12);
  color:var(--text);
}
.orka-v3-scope[data-theme="dark"]{
  --surface:#101928;--surface2:#152032;--border:rgba(255,255,255,.12);
  --text:#f8fafc;--text2:#d7dfeb;--text3:#94a3b8;
  --shadow:rgba(0,0,0,.42);
  color:var(--text);
}
`;
    style.textContent = scopeCss(RAW_CSS + CSS_EFFORT_DENSITIES, SCOPE) + extra;
    document.head.appendChild(style);
    __cssInjected = true;
  }, []);
}

/* ———————————————————————— Shared subcomponents ——————————————————————— */

function TokenDropdown({ kind, value, onChange, compact = false, priorities, efforts }) {
  const options = kind === "priority" ? priorities : efforts;
  const config = {
    priority: { cls: "priority-token", Icon: Icons.Flag },
    effort:   { cls: "effort-token",   Icon: Icons.Gauge },
  }[kind];
  const meta = resolve(options, value);
  const TokenIcon = config.Icon;
  const format = (option) => compact ? option.short : `${option.short} · ${option.label}`;
  return (
    <label className={cx("token-select", config.cls)} style={cssVars([["--token-color", meta.color]])} aria-label={`Change ${kind}`}>
      <TokenIcon />
      <span className="token-dot" />
      <select value={value} onChange={(event) => onChange(Number(event.target.value))}>
        {options.map((option) => <option key={option.value} value={option.value}>{format(option)}</option>)}
      </select>
    </label>
  );
}

function WorkAreaDropdown({ workAreas, personalSubAreas, value, onChange, subAreaId, onSubAreaChange, compact = false }) {
  const safeWorkAreas = (workAreas && workAreas.length > 0)
    ? workAreas
    : [{ work_area_id: '', label: 'No work area', short: 'NONE', color: '#64748b', is_personal: false }];
  const selected = resolve(safeWorkAreas, value, 0, "work_area_id") || safeWorkAreas[0];
  const safeSubs = personalSubAreas || [];
  const selectedSub = safeSubs.find(s => s.sub_area_id === subAreaId);
  return (
    <div className={cx("workarea-stack", selected.is_personal && "has-subarea")}>
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cx("token-select", "workarea-token", "workarea-popover-trigger")}
            style={cssVars([["--token-color", selected.color]])}
            aria-label="Change work area"
          >
            <Icons.Briefcase />
            <span className="token-dot" />
            <span className="workarea-label">{selected.label}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-56 p-1">
          <div className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 text-slate-500">Work area</div>
          {safeWorkAreas.map((area) => (
            <button
              key={area.work_area_id}
              type="button"
              onClick={() => onChange(area.work_area_id)}
              className="w-full text-left px-2 py-1.5 rounded text-xs flex items-center gap-2 hover:bg-slate-100 dark:hover:bg-slate-800"
              data-testid={`v5-wa-option-${area.work_area_id}`}
            >
              {value === area.work_area_id ? <Check className="w-3 h-3 text-emerald-500" /> : <span className="w-3" />}
              <span
                className="w-2.5 h-2.5 rounded-full flex-none border"
                style={{ background: area.color || '#94a3b8', borderColor: 'rgba(15,23,42,.18)' }}
              />
              <span className="truncate">{area.label}</span>
              {area.is_personal && <span className="ml-auto text-[9px] font-bold text-slate-500">personal</span>}
            </button>
          ))}
        </PopoverContent>
      </Popover>
      {selected.is_personal && safeSubs.length > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={cx("token-select", "subarea-token", "workarea-popover-trigger")}
              style={cssVars([["--token-color", selected.color]])}
              aria-label="Change personal sub-area"
            >
              <span className="token-dot" />
              <span className="workarea-label">{selectedSub ? selectedSub.label : "Sub-area"}</span>
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-44 p-1">
            <div className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 text-slate-500">Sub-area</div>
            {safeSubs.map((area) => (
              <button
                key={area.sub_area_id}
                type="button"
                onClick={() => onSubAreaChange(area.sub_area_id)}
                className="w-full text-left px-2 py-1.5 rounded text-xs flex items-center gap-2 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                {subAreaId === area.sub_area_id ? <Check className="w-3 h-3 text-emerald-500" /> : <span className="w-3" />}
                <span className="truncate">{area.label}</span>
              </button>
            ))}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}

function StatusMorph({ current, onChoose, onClose, mobile = false, statuses }) {
  return (
    <div className={cx("status-morph", mobile && "mobile-status-morph")}>
      <div className="status-grid">
        {statuses.map((status) => (
          <button key={status.value} className={cx("status-choice", current === status.value && "active")} type="button" style={cssVars([["--choice-color", status.color]])} onClick={() => onChoose(status.value)}>
            <span className="status-dot" />
            <span className="status-label">{status.label}</span>
          </button>
        ))}
      </div>
      <button className="status-morph-close" type="button" onClick={onClose}>Close</button>
    </div>
  );
}

/** Portal-rendered floating status morph. Anchored to `anchorRef` button's
 *  bounding rect; escapes card overflow so it renders on top of the 60mins
 *  Card. Closes on outside-click, Escape, window scroll/resize. */
function FloatingStatusMorph({ current, onChoose, onClose, statuses, anchorRef, themeAttr }) {
  const panelRef = useRef(null);
  const [rect, setRect] = useState(null);

  useEffect(() => {
    const recompute = () => {
      if (!anchorRef?.current) return;
      const r = anchorRef.current.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, right: r.right, bottom: r.bottom, width: r.width, height: r.height });
    };
    recompute();
    window.addEventListener('resize', recompute);
    window.addEventListener('scroll', recompute, true);
    return () => {
      window.removeEventListener('resize', recompute);
      window.removeEventListener('scroll', recompute, true);
    };
  }, [anchorRef]);

  useEffect(() => {
    const onDown = (e) => {
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      if (anchorRef?.current && anchorRef.current.contains(e.target)) return;
      onClose();
    };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose, anchorRef]);

  if (!rect || typeof document === 'undefined') return null;

  const PANEL_W = Math.max(rect.width, 220);
  const PANEL_H = 150;
  // Prefer below, flip above if not enough space
  let top = rect.bottom + 6;
  if (top + PANEL_H > window.innerHeight - 8) top = Math.max(8, rect.top - PANEL_H - 6);
  let left = rect.right - PANEL_W;
  if (left < 8) left = Math.max(8, rect.left);
  if (left + PANEL_W > window.innerWidth - 8) left = window.innerWidth - PANEL_W - 8;

  const node = (
    <div
      className={SCOPE}
      data-theme={themeAttr || 'light'}
      data-orka-v3-portal="true"
      style={{ position: 'fixed', top: 0, left: 0, zIndex: 2000, pointerEvents: 'none' }}
    >
      <div
        ref={panelRef}
        className="status-morph floating-status-morph"
        style={{
          position: 'fixed', top, left, width: PANEL_W, minWidth: PANEL_W,
          pointerEvents: 'auto',
          boxShadow: '0 22px 48px color-mix(in srgb, var(--status-color) 28%, rgba(0,0,0,0.24))',
          '--status-color': statuses.find((s) => s.value === current)?.color || '#64748b',
        }}
      >
        <div className="status-grid">
          {statuses.map((status) => (
            <button
              key={status.value}
              className={cx('status-choice', current === status.value && 'active')}
              type="button"
              style={cssVars([['--choice-color', status.color]])}
              onClick={(e) => { e.stopPropagation(); onChoose(status.value); }}
            >
              <span className="status-dot" />
              <span className="status-label">{status.label}</span>
            </button>
          ))}
        </div>
        <button className="status-morph-close" type="button" onClick={(e) => { e.stopPropagation(); onClose(); }}>Close</button>
      </div>
    </div>
  );
  return ReactDOM.createPortal(node, document.body);
}

function FieldInput({ label, type = "text", value, onChange, placeholder = "", textarea = false, className = "" }) {
  return (
    <label className={cx("field-input", className)}>
      <span>{label}</span>
      {textarea
        ? <textarea value={value || ''} placeholder={placeholder} onChange={(event) => onChange(event.target.value)} />
        : <input type={type} value={value || ''} placeholder={placeholder} onChange={(event) => onChange(event.target.value)} />}
    </label>
  );
}

function DescriptionEditor({ initialValue, onChange }) {
  const editorRef = useRef(null);
  const [html, setHtml] = useState(`<p>${initialValue || ''}</p>`);
  const [codeMode, setCodeMode] = useState(false);

  const syncFromEditor = () => {
    if (editorRef.current) {
      const next = editorRef.current.innerHTML;
      setHtml(next);
      if (onChange) onChange(next);
    }
  };

  const runCommand = (command, value = null) => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.focus();
    document.execCommand(command, false, value);
    syncFromEditor();
  };

  const insertBlock = (htmlToInsert) => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.focus();
    document.execCommand("insertHTML", false, htmlToInsert);
    syncFromEditor();
  };

  return (
    <div className="description-editor">
      <div className="description-toolbar">
        <button className="tool-btn" type="button" onMouseDown={(e) => { e.preventDefault(); runCommand("bold"); }}>B</button>
        <button className="tool-btn" type="button" onMouseDown={(e) => { e.preventDefault(); runCommand("italic"); }}><em>I</em></button>
        <button className="tool-btn" type="button" onMouseDown={(e) => { e.preventDefault(); runCommand("underline"); }}><u>U</u></button>
        <span className="toolbar-separator" />
        <button className="tool-btn" type="button" onMouseDown={(e) => { e.preventDefault(); runCommand("formatBlock", "p"); }}>P</button>
        <button className="tool-btn" type="button" onMouseDown={(e) => { e.preventDefault(); runCommand("formatBlock", "h2"); }}>H2</button>
        <button className="tool-btn" type="button" onMouseDown={(e) => { e.preventDefault(); runCommand("formatBlock", "h3"); }}>H3</button>
        <span className="toolbar-separator" />
        <button className="tool-btn" type="button" onMouseDown={(e) => { e.preventDefault(); runCommand("insertUnorderedList"); }}>• List</button>
        <button className="tool-btn" type="button" onMouseDown={(e) => { e.preventDefault(); runCommand("insertOrderedList"); }}>1. List</button>
        <button className="tool-btn" type="button" onMouseDown={(e) => { e.preventDefault(); runCommand("formatBlock", "blockquote"); }}>Quote</button>
        <span className="toolbar-separator" />
        <button className="tool-btn" type="button" onMouseDown={(e) => { e.preventDefault(); runCommand("justifyLeft"); }}>Left</button>
        <button className="tool-btn" type="button" onMouseDown={(e) => { e.preventDefault(); runCommand("justifyCenter"); }}>Center</button>
        <button className="tool-btn" type="button" onMouseDown={(e) => { e.preventDefault(); runCommand("removeFormat"); }}>Clear</button>
        <button className={cx("tool-btn", codeMode && "active")} type="button" onClick={() => setCodeMode((c) => !c)}>HTML</button>
      </div>
      {codeMode ? (
        <textarea className="description-code" value={html} onChange={(e) => { setHtml(e.target.value); if (onChange) onChange(e.target.value); }} placeholder="Write raw HTML here…" />
      ) : (
        <div className="block-editor-shell">
          <div className="block-sidebar">
            <button className="block-type-btn" type="button" onClick={() => insertBlock("<h2>New heading</h2>")}>+ Heading</button>
            <button className="block-type-btn" type="button" onClick={() => insertBlock("<p>New paragraph</p>")}>+ Paragraph</button>
            <button className="block-type-btn" type="button" onClick={() => insertBlock("<ul><li>List item</li></ul>")}>+ Bullet list</button>
            <button className="block-type-btn" type="button" onClick={() => insertBlock("<blockquote>Quote or note</blockquote>")}>+ Quote</button>
          </div>
          <div ref={editorRef} className="description-box" contentEditable suppressContentEditableWarning dangerouslySetInnerHTML={{ __html: html }} onInput={syncFromEditor} />
        </div>
      )}
    </div>
  );
}

function ResponsiblePicker({ value, onChange, people = DEMO_PEOPLE }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const visible = people.filter((n) => String(n).toLowerCase().includes(query.toLowerCase()));
  return (
    <div className="responsible-picker">
      <button type="button" className="responsible-trigger" onClick={() => setOpen((c) => !c)}>
        <span className="person-chip">{value || '—'}</span><Icons.Chevron />
      </button>
      {open && (
        <div className="responsible-menu">
          <div className="search-row"><Icons.Search /><input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search people…" /></div>
          <div className="person-list">
            {visible.map((n) => (
              <button type="button" key={n} className={cx("person-option", value === n && "active")} onClick={() => { onChange(n); setOpen(false); }}>
                <span>{n}</span><span>{value === n ? "Selected" : "Select"}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ObserverPicker({ selected = [], onChange, people = DEMO_PEOPLE }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const visible = people.filter((n) => String(n).toLowerCase().includes(query.toLowerCase()));
  const toggle = (n) => {
    const next = selected.includes(n) ? selected.filter((x) => x !== n) : [...selected, n];
    if (onChange) onChange(next);
  };
  return (
    <div className="observer-picker">
      <button type="button" className="observer-trigger" onClick={() => setOpen((c) => !c)}>
        <span className="chips">{selected.map((n) => <span className="person-chip" key={n}>{n}</span>)}</span><Icons.Chevron />
      </button>
      {open && (
        <div className="observer-menu">
          <div className="search-row"><Icons.Search /><input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search observers…" /></div>
          <div className="person-list">
            {visible.map((n) => (
              <button type="button" key={n} className={cx("person-option", selected.includes(n) && "active")} onClick={() => toggle(n)}>
                <span>{n}</span><span>{selected.includes(n) ? "Remove" : "Add"}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MeetingSection({ title, link }) {
  return (
    <div className="meeting-card">
      <div className="meeting-title"><Icons.Video /> {title}</div>
      <div className="meeting-actions">
        {link && <a className="meeting-btn meet" href={link} target="_blank" rel="noreferrer"><Icons.Video /> Join Meet</a>}
        <button className="meeting-btn calendar" type="button"><Icons.Calendar /> Open Calendar</button>
        <button className="meeting-btn email" type="button"><Icons.Mail /> Email attendees</button>
      </div>
    </div>
  );
}

function PeopleSection({ owner, setOwner, observers, setObservers, people }) {
  return (
    <section className="section-block">
      <div className="section-head">
        <div className="section-head-left"><Icons.Users /> People</div>
      </div>
      <div className="people-grid">
        <div className="person-field">
          <span className="person-label">Responsible person</span>
          <ResponsiblePicker value={owner} onChange={setOwner} people={people} />
        </div>
        <div className="person-field">
          <span className="person-label">Observers</span>
          <ObserverPicker selected={observers} onChange={setObservers} people={people} />
        </div>
      </div>
    </section>
  );
}

function RecurringLogicBox({ rule, onChange }) {
  const parts = String(rule || "").split(";");
  const get = (k) => (parts.find((p) => p.startsWith(`${k}=`)) || "").split("=")[1] || "";
  const [frequency, setFrequency] = useState(get("FREQ") || "Weekly");
  const [interval, setInterval] = useState(get("INTERVAL") || "1");
  const [until, setUntil] = useState(get("UNTIL") || "");
  useEffect(() => {
    if (onChange) onChange(`FREQ=${frequency};INTERVAL=${interval}${until ? `;UNTIL=${until}` : ""}`);
  }, [frequency, interval, until]); // eslint-disable-line
  return (
    <div className="logic-grid">
      <label className="field-input">
        <span>Frequency</span>
        <select value={frequency} onChange={(e) => setFrequency(e.target.value)}>
          <option>Daily</option><option>Weekly</option><option>Monthly</option><option>Custom</option>
        </select>
      </label>
      <FieldInput label="Repeat Every" type="number" value={interval} onChange={setInterval} />
      <FieldInput label="End Date" type="date" value={until} onChange={setUntil} />
    </div>
  );
}

function AdvancedFields({ task, update }) {
  return (
    <div className="advanced-grid">
      <FieldInput label="Parent Task ID" value={task.task_parent_task_id} onChange={(v) => update({ task_parent_task_id: v })} placeholder="parent_task_id" />
      <FieldInput label="Continuation Task ID" value={task.task_continuation_task_id} onChange={(v) => update({ task_continuation_task_id: v })} placeholder="continuation_task_id" />
      <FieldInput label="Big Picture Link" value={task.task_big_picture_id} onChange={(v) => update({ task_big_picture_id: v })} placeholder="big_picture_id" />
      <FieldInput label="Next Steps" value={task.task_next_steps} onChange={(v) => update({ task_next_steps: v })} placeholder="Follow-up text" textarea className="wide-field" />
      <FieldInput label="Impact" value={task.task_impact} onChange={(v) => update({ task_impact: v })} placeholder="Free-text impact" textarea />
      <FieldInput label="Blocked Reason" value={task.task_blocked_reason} onChange={(v) => update({ task_blocked_reason: v })} placeholder="Set when status = blocked" textarea />
      <FieldInput label="Related Links" value={Array.isArray(task.task_related_links) ? task.task_related_links.join('\n') : task.task_related_links} onChange={(v) => update({ task_related_links: v.split('\n').filter(Boolean) })} placeholder="One URL per line" textarea />
    </div>
  );
}

function OpenBody({ task, update, people, isMeeting, setIsMeeting }) {
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [recurringOpen, setRecurringOpen] = useState(Boolean(task.task_recurrence_rule));
  const [meetingOpen, setMeetingOpen] = useState(isMeeting);
  const isRecurring = Boolean(task.task_recurrence_rule);
  return (
    <div className="open-body">
      <div className="meta-strip">
        <span className="meta-subtle"><Icons.Hash />{task.id || task.task_id}</span>
        {task.created && <span className="meta-subtle">Created {task.created}</span>}
        {task.rescheduleCount != null && <span className="meta-subtle"><Icons.Repeat />Rescheduled {task.rescheduleCount}×</span>}
        <label className="hidden-check"><input type="checkbox" checked={!!task.task_hidden} onChange={(e) => update({ task_hidden: e.target.checked })} /> Is hidden?</label>
      </div>
      <DescriptionEditor initialValue={task.description || task.task_description} onChange={(v) => update({ task_description: v })} />
      <div className="date-time-grid">
        <FieldInput label="Date for delivery" type="date" value={task.date || task.task_delivery_date} onChange={(v) => update({ task_delivery_date: v })} />
        <FieldInput label="Start time" type="time" value={task.start || task.task_start_time} onChange={(v) => update({ task_start_time: v })} />
        <FieldInput label="End time" type="time" value={task.end || task.task_end_time} onChange={(v) => update({ task_end_time: v })} />
      </div>
      <PeopleSection
        owner={task.owner || task.responsible_person_name || ''}
        setOwner={(v) => update({ responsible_person_name: v })}
        observers={Array.isArray(task.task_observers) ? task.task_observers : (task.task_observers ? String(task.task_observers).split(',').map(s => s.trim()) : [])}
        setObservers={(v) => update({ task_observers: v })}
        people={people}
      />
      <section className="section-block recurring">
        <div className="toggle-section">
          <label className="checkbox-row">
            <input type="checkbox" checked={isRecurring} onChange={(e) => { if (e.target.checked) { update({ task_recurrence_rule: 'FREQ=WEEKLY;INTERVAL=1' }); setRecurringOpen(true); } else { update({ task_recurrence_rule: '' }); setRecurringOpen(false); } }} /> Is Recurring?
          </label>
          <button className={cx("section-expander-arrow", recurringOpen && "open")} type="button" onClick={() => isRecurring && setRecurringOpen((c) => !c)}><Icons.Chevron /></button>
        </div>
        {isRecurring && recurringOpen && <RecurringLogicBox rule={task.task_recurrence_rule} onChange={(v) => update({ task_recurrence_rule: v })} />}
      </section>
      <section className="section-block meeting">
        <div className="toggle-section">
          <label className="checkbox-row">
            <input type="checkbox" checked={isMeeting} onChange={(e) => { setIsMeeting(e.target.checked); setMeetingOpen(e.target.checked); update({ is_meeting: e.target.checked }); }} /> Is Meeting?
          </label>
          <button className={cx("section-expander-arrow", meetingOpen && "open")} type="button" onClick={() => isMeeting && setMeetingOpen((c) => !c)}><Icons.Chevron /></button>
        </div>
        {isMeeting && meetingOpen && <MeetingSection title={task.title || task.task_title} link={task.google_meet_url} />}
      </section>
      <div className="expander">
        <button type="button" onClick={() => setAdvancedOpen((c) => !c)}>
          <span className="section-head-left"><Icons.Sliders /> Advanced Details</span>
          <span className={cx("section-expander-arrow", advancedOpen && "open")} aria-hidden="true"><Icons.Chevron /></span>
        </button>
        {advancedOpen && <div className="expander-body"><AdvancedFields task={task} update={update} /></div>}
      </div>
    </div>
  );
}

/* ———————————————————————— Core task card ——————————————————————— */

export function V3TaskCard({
  task,
  width = 720,
  density = "standard",
  workAreas = V3_WORK_AREAS,
  personalSubAreas = V3_PERSONAL_SUB_AREAS,
  people,
  onChange,
  onEditLink,
  onEdit,
  hideGrip = false,
  forceOpen = false,
  dragHandleRef,
  dragListeners,
}) {
  useV3TaskCardPreviewStyles();

  const t = task || {};
  const [draft, setDraft] = useState(t);
  useEffect(() => { setDraft(t); }, [t.task_id || t.id]); // eslint-disable-line

  const update = (patch) => {
    setDraft((prev) => ({ ...prev, ...patch }));
    if (onChange) onChange(patch);
  };

  const [statusOpen, setStatusOpen] = useState(false);
  const [cardOpen, setCardOpen] = useState(forceOpen);
  const [titleEditing, setTitleEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState(t.title || t.task_title || "");

  // Anchor refs for the floating status morph (desktop + mobile).
  const statusBtnRef = useRef(null);
  const mobileStatusBtnRef = useRef(null);
  const [themeAttr, setThemeAttr] = useState("light");
  useEffect(() => {
    // Infer theme from closest .orka-v3-scope ancestor so the portal's
    // variables match the host card's palette.
    const el = statusBtnRef.current || mobileStatusBtnRef.current;
    if (!el) return;
    const scope = el.closest('.orka-v3-scope');
    setThemeAttr((scope && scope.getAttribute('data-theme')) || 'light');
  }, [statusOpen]);

  useEffect(() => { setDraftTitle(t.title || t.task_title || ""); }, [t.title, t.task_title]);

  const priority = draft.task_priority != null ? draft.task_priority : (t.priority ?? 3);
  const workarea = draft.work_area_id || workAreas[0]?.work_area_id || "";
  const personalSubAreaId = draft.personal_sub_area_id || "";
  const effort = draft.task_estimated_effort != null ? draft.task_estimated_effort : 3;
  const status = draft.task_status_key || mapStatusToKey(draft.task_status) || "todo";
  const isMeeting = Boolean(draft.is_meeting);
  const setIsMeeting = (v) => update({ is_meeting: v });
  // Effort-tier densities (15/30/60/180/360/600) get the new collapse
  // ceiling + chevron expander behaviour. Others (standard/calm) keep the
  // legacy chevron-toggles-in-place behaviour.
  const isEffortDensity = ['15min', '30min', '60min', '180min', '360min', '600min'].includes(density);
  const showFadePreview = ['180min', '360min', '600min'].includes(density);

  const mode = getV3WidthMode(width);
  const isTightSquash = mode === "squashed" && width < 560;
  const priorityMeta = resolve(V3_PRIORITIES, priority);
  const waMeta = resolve(workAreas, workarea, 0, "work_area_id") || { color: "#64748b" };
  const statusMeta = resolve(V3_STATUSES, status);
  const cardStyle = cssVars([
    ["--priority-color", priorityMeta.color],
    ["--workarea-color", waMeta.color || "#64748b"],
    ["--status-color", statusMeta.color],
  ]);

  const saveTitle = () => { update({ task_title: draftTitle }); setTitleEditing(false); };
  const cancelTitle = () => { setDraftTitle(t.task_title || t.title || ""); setTitleEditing(false); };

  const statusZone = (
    <div className="status-zone">
      <button
        ref={statusBtnRef}
        className="status"
        type="button"
        onClick={() => setStatusOpen((v) => !v)}
        data-testid={`v3-status-toggle-${t.task_id || t.id || ''}`}
      >
        <span>{statusMeta.label}</span><span className="status-ring" />
      </button>
      <button className={cx("card-toggle", cardOpen && "open")} type="button" onClick={() => setCardOpen((c) => !c)}>
        <Icons.Chevron />
      </button>
    </div>
  );

  const forceCompact = mode !== "regular" || density === "15min" || density === "30min";
  const controls = (
    <>
      <WorkAreaDropdown workAreas={workAreas} personalSubAreas={personalSubAreas} value={workarea}
        onChange={(v) => update({ work_area_id: v })}
        subAreaId={personalSubAreaId}
        onSubAreaChange={(v) => update({ personal_sub_area_id: v })}
        compact={forceCompact} />
      <div className="pe-stack">
        <TokenDropdown kind="priority" value={priority} onChange={(v) => update({ task_priority: v })} compact={forceCompact} priorities={V3_PRIORITIES} efforts={V3_EFFORTS} />
        <TokenDropdown kind="effort"   value={effort}   onChange={(v) => update({ task_estimated_effort: v })} compact={forceCompact} priorities={V3_PRIORITIES} efforts={V3_EFFORTS} />
      </div>
      {statusZone}
    </>
  );

  const header = (
    <>
      <div className={cx("title-row", titleEditing && "editing")}>
        {isMeeting && <span className="meeting-title-icon"><Icons.Calendar /></span>}
        <input className="title-input" value={draftTitle} readOnly={!titleEditing} onFocus={() => setTitleEditing(true)} onChange={(e) => setDraftTitle(e.target.value)} />
        <div className="title-actions">
          <button className="inline-action save-title" type="button" onClick={saveTitle}>✓</button>
          <button className="inline-action cancel-title" type="button" onClick={cancelTitle}>×</button>
        </div>
      </div>
      <div className="header-meta">
        <span>
          <Icons.Calendar />
          <span className="date-picker-wrap">
            <input className="header-date-picker" type="date" value={draft.task_delivery_date || draft.date || ""} onChange={(e) => update({ task_delivery_date: e.target.value })} />
            <span className="date-chevron-input-wrap">
              <input className="date-arrow-input" type="date" value={draft.task_delivery_date || draft.date || ""} onChange={(e) => update({ task_delivery_date: e.target.value })} aria-label="Open date picker" />
              <Icons.Chevron className="date-chevron" />
            </span>
          </span>
        </span>
        <span>
          <Icons.Clock />
          <span className="time-picker-wrap">
            <span className="time-text">{(draft.task_start_time || draft.start || "--:--")}–{(draft.task_end_time || draft.end || "--:--")}</span>
            <span className="time-chevron-input-wrap">
              <input className="time-arrow-input" type="date" value={draft.task_delivery_date || draft.date || ""} onChange={(e) => update({ task_delivery_date: e.target.value })} aria-label="Open date picker" />
              <Icons.Chevron className="time-chevron" />
            </span>
          </span>
        </span>
      </div>
    </>
  );

  const renderBody = () => {
    if (isEffortDensity) {
      // Render body whenever expanded, OR when the density is large enough
      // to show the fade-preview underneath the header.
      if (cardOpen || showFadePreview) {
        return <OpenBody task={{ ...draft, id: draft.task_id || draft.id }} update={update} people={people} isMeeting={isMeeting} setIsMeeting={setIsMeeting} />;
      }
      return null;
    }
    return cardOpen ? <OpenBody task={{ ...draft, id: draft.task_id || draft.id }} update={update} people={people} isMeeting={isMeeting} setIsMeeting={setIsMeeting} /> : null;
  };

  const editHref = onEditLink || `/tasks/${t.task_id || t.id || ""}/edit`;
  const expandedClass = isEffortDensity && cardOpen ? "is-expanded" : null;

  return (
    <>
      <div className={cx("card", `density-${density}`, `mode-${mode}`, isTightSquash && "squash-tight", expandedClass)} style={cardStyle} data-testid={`v3-task-card-${t.task_id || t.id}`} data-density={density} data-expanded={cardOpen ? "true" : "false"}>
        <a className="edit" href={editHref} aria-label="Open full task edit" onClick={(e) => { if (onEdit) { e.preventDefault(); onEdit(t); } else if (onEditLink === '#') e.preventDefault(); }}><Icons.Pencil /></a>
        <div className="main">
          {!hideGrip && (
            <div className="grip" ref={dragHandleRef} {...(dragListeners || {})}><Icons.Grip /></div>
          )}
          <div className="bar" />
          <div className="header-content">
            {header}
            <div className="header-controls"><div className="actions">{controls}</div></div>
          </div>
        </div>
        {renderBody()}
      </div>
      <div className={cx("mobile-card", `mode-${mode}`)} style={cardStyle}>
        <a className="edit" href={editHref} aria-label="Open full task edit" onClick={(e) => { if (onEdit) { e.preventDefault(); onEdit(t); } else if (onEditLink === '#') e.preventDefault(); }}><Icons.Pencil /></a>
        <div className="mobile-top">
          <div className="mobile-grip" ref={dragHandleRef} {...(dragListeners || {})}><Icons.Grip /></div>
          <div className="mobile-bar" />
          <div className="mobile-title-wrap">{header}</div>
        </div>
        <div className="mobile-controls">
          <div className="mobile-workarea">
            <WorkAreaDropdown workAreas={workAreas} personalSubAreas={personalSubAreas} value={workarea}
              onChange={(v) => update({ work_area_id: v })}
              subAreaId={personalSubAreaId}
              onSubAreaChange={(v) => update({ personal_sub_area_id: v })}
              compact />
          </div>
          <TokenDropdown kind="priority" value={priority} onChange={(v) => update({ task_priority: v })} compact priorities={V3_PRIORITIES} efforts={V3_EFFORTS} />
          <TokenDropdown kind="effort"   value={effort}   onChange={(v) => update({ task_estimated_effort: v })} compact priorities={V3_PRIORITIES} efforts={V3_EFFORTS} />
          <div className="status-zone">
            <button
              ref={mobileStatusBtnRef}
              className="mobile-status"
              type="button"
              onClick={() => setStatusOpen((v) => !v)}
              data-testid={`v3-status-toggle-mobile-${t.task_id || t.id || ''}`}
            >
              <span>{statusMeta.label}</span><span className="status-ring" />
            </button>
            <button className={cx("card-toggle", cardOpen && "open")} type="button" onClick={() => setCardOpen((c) => !c)}>
              <Icons.Chevron />
            </button>
          </div>
        </div>
        {renderBody()}
      </div>
      {statusOpen && (
        <FloatingStatusMorph
          current={status}
          statuses={V3_STATUSES}
          anchorRef={mode === 'mobile' ? mobileStatusBtnRef : statusBtnRef}
          themeAttr={themeAttr}
          onChoose={(v) => { update({ task_status_key: v, task_status: mapKeyToStatus(v) }); setStatusOpen(false); }}
          onClose={() => setStatusOpen(false)}
        />
      )}
    </>
  );
}

/* —————————— legacy status-string ↔ canonical-key mappers —————————— */
function mapStatusToKey(s) {
  switch (s) {
    case "To Do": return "todo";
    case "In Progress": return "inProgress";
    case "Done": return "done";
    case "Blocked": return "blocked";
    case "Partial": return "inProgress";
    default: return s;
  }
}
function mapKeyToStatus(k) {
  switch (k) {
    case "todo": return "To Do";
    case "inProgress": return "In Progress";
    case "done": return "Done";
    case "blocked": return "Blocked";
    default: return k;
  }
}

/* ———————————————————————— Preview (design-system showcase) ——————————— */

function VariantPanel({ variant, width, workAreas, personalSubAreas }) {
  const mode = getV3WidthMode(width);
  return (
    <section className="panel" data-theme={variant.theme}>
      <span className="variant-label">{variant.label}</span>
      <div className="variant-frame" style={{ maxWidth: width }}>
        <V3TaskCard
          width={width}
          density={variant.density}
          workAreas={workAreas}
          personalSubAreas={personalSubAreas}
          task={{
            id: DEMO_TASK.id,
            task_id: DEMO_TASK.id,
            title: DEMO_TASK.title,
            task_title: DEMO_TASK.title,
            task_description: DEMO_TASK.description,
            task_delivery_date: DEMO_TASK.date,
            task_start_time: DEMO_TASK.start,
            task_end_time: DEMO_TASK.end,
            task_priority: 1,
            task_estimated_effort: 3,
            task_status_key: "todo",
            work_area_id: workAreas[0]?.work_area_id,
            responsible_person_name: DEMO_TASK.owner,
            created: DEMO_TASK.created,
            rescheduleCount: DEMO_TASK.rescheduleCount,
          }}
          onEditLink="#"
        />
      </div>
      <div className="variant-width-note">Preview width: {width}px · Mode: {mode}</div>
    </section>
  );
}

/* —————————————————————————————————————————————————————————————————————————
 * The deprecated V3 showcase exports (OrkaTaskCardV3Preview / V3EffortPreview /
 * V360minPreview) were retired Feb 2026. The single canonical task-card
 * showcase is now `OrkaTaskCardV5Preview`. The render engine `V3TaskCard`
 * remains exported for in-product consumers (Daily Dashboard, Tasks page,
 * AI chat) — it is the V5 task card; the legacy "V3" name is kept only as
 * an internal symbol to avoid a blast-radius rename of every import site.
 * ——————————————————————————————————————————————————————————————————————— */
export function OrkaTaskCardV5Preview({ theme = 'light' }) {
  useV3TaskCardPreviewStyles();
  const t = (theme === 'dark') ? 'dark' : 'light';
  const [width, setWidth] = useState(900);
  const widthLabel = useMemo(() => `${width}px`, [width]);
  const tiers = [
    { density: '15min',  label: '15 min · E1',  px: 30   },
    { density: '30min',  label: '30 min · E2',  px: 60   },
    { density: '60min',  label: '60 min · E3',  px: 120  },
    { density: '180min', label: '180 min · E5', px: 360  },
    { density: '360min', label: '360 min · E8', px: 720  },
    { density: '600min', label: '600 min · E13',px: 1200 },
  ];
  return (
    <div className={SCOPE} data-theme={t}>
      <div className="preview" style={{ background: t === 'dark' ? '#0b1424' : '#eef3f8' }}>
        <main className="page">
          <div className="intro" style={{ color: t === 'dark' ? '#f8fafc' : '#0f172a' }}>
            <h1>Task Card · V5 — unified canonical surface</h1>
            <p>
              Single canonical task-card style. One source of truth for every
              density (effort-driven height), the day-planner 60-min spotlight,
              and the width-responsive standard / calm variants. Theme follows
              the design-system page setting (Light by default, switch via the
              top-right Light / Dark toggle).
            </p>
          </div>

          {/* Sub 1 — Base width-responsive */}
          <div className="controls" style={{ background: t === 'dark' ? '#0f1728' : '#ffffff', borderColor: t === 'dark' ? 'rgba(255,255,255,.08)' : 'rgba(15,23,42,.10)' }}>
            <div className="controls-top">
              <div><div className="control-label" style={{ color: t === 'dark' ? '#e2e8f0' : '#0f172a' }}>V5 · Base — responsive width</div></div>
              <div className="control-meta">
                <span className="badge">{widthLabel}</span>
                <span className="badge">{getV3WidthMode(width)}</span>
              </div>
            </div>
            <input className="range" type="range" min="360" max="1100" value={width} onChange={(e) => setWidth(Number(e.target.value))} />
          </div>
          <div className="variant-grid">
            {VARIANTS.filter((v) => v.theme === t && (v.density === 'standard' || v.density === 'calm')).map((variant) => (
              <VariantPanel key={variant.key} variant={variant} width={width} workAreas={V3_WORK_AREAS} personalSubAreas={V3_PERSONAL_SUB_AREAS} />
            ))}
          </div>

          {/* Sub 2 — 60-min spotlight (day planner default) */}
          <div className="intro" style={{ marginTop: 28, color: t === 'dark' ? '#f8fafc' : '#0f172a' }}>
            <h1 style={{ fontSize: 22 }}>V5 · 60-min spotlight</h1>
            <p>
              Day-planner default. Card height = 1 hour = 4 × 15-min slots = 120 px.
              Stretches proportionally inside a time-sized rail slot (a 3-hour task fills 360 px).
            </p>
          </div>
          <div className="variant-grid">
            {VARIANTS.filter((v) => v.theme === t && v.density === '60min').map((variant) => (
              <VariantPanel key={variant.key} variant={variant} width={Math.min(width, 720)} workAreas={V3_WORK_AREAS} personalSubAreas={V3_PERSONAL_SUB_AREAS} />
            ))}
          </div>

          {/* Sub 3 — Effort tiers */}
          <div className="intro" style={{ marginTop: 28, color: t === 'dark' ? '#f8fafc' : '#0f172a' }}>
            <h1 style={{ fontSize: 22 }}>V5 · Effort tiers (15 / 30 / 60 / 180 / 360 / 600 min)</h1>
            <p>
              Six canonical densities, one per effort score. Header layout is
              fixed at a 120 px ceiling. Variants ≤ 60 min collapse the header
              itself; variants &gt; 60 min keep the header at its ceiling and
              show the interior peeking through with a transparency fade.
            </p>
          </div>
          <div className="variant-grid">
            {tiers.map((tier) => (
              <section className="panel" data-theme={t} key={tier.density}>
                <span className="variant-label">{tier.label} · {tier.px}px</span>
                <div className="variant-frame" style={{ height: tier.px + 32, position: 'relative', overflow: 'visible' }}>
                  <div style={{ position: 'absolute', top: 8, left: 8, right: 8, height: tier.px }}>
                    <V3TaskCard
                      density={tier.density}
                      width={720}
                      workAreas={V3_WORK_AREAS}
                      personalSubAreas={V3_PERSONAL_SUB_AREAS}
                      task={{
                        id: `v5-tier-${tier.density}`, task_id: `v5-tier-${tier.density}`,
                        task_title: `${tier.label} demo task`,
                        task_description: 'Click the chevron in the top-right to expand and reveal the open-body editor.',
                        task_delivery_date: '2026-05-05',
                        task_start_time: '08:30', task_end_time: '09:30',
                        task_priority: 1,
                        task_estimated_effort: ({'15min':1,'30min':2,'60min':3,'180min':5,'360min':8,'600min':13}[tier.density]),
                        task_status_key: 'todo',
                        work_area_id: V3_WORK_AREAS[0]?.work_area_id,
                        responsible_person_name: 'Joseph',
                        created: '2026-05-04',
                      }}
                      onEditLink="#"
                    />
                  </div>
                </div>
                <div className="variant-width-note">Slot height: {tier.px}px · Header ceiling: 120px · {tier.px > 120 ? 'fade preview visible' : 'header-only collapse'}</div>
              </section>
            ))}
          </div>
        </main>
      </div>
    </div>
  );
}

/* V5 alias exports — preferred public API for new consumers. The V3*
   names remain available as legacy aliases. */
export { V3TaskCard as V5TaskCard, useV3TaskCardPreviewStyles as useV5TaskCardStyles };

export default OrkaTaskCardV5Preview;
